// Firestore + Auth helpers for the chat feature.
// All collections live under /chatProfiles and /conversations so they don't collide
// with the existing /users/{uid} private workspace.
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import {
  arrayUnion,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocFromServer,
  getDocs,
  Timestamp,
  setDoc,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit as fbLimit,
  runTransaction,
  serverTimestamp,
  onSnapshot,
} from "firebase/firestore";
import { auth, db } from "../firebase";
import {
  backupLocalKeys,
  decryptMessage as decryptZkMessage,
  encryptMessage as encryptZkMessage,
  ensureIdentityKeyPair,
  recoverLocalKeys,
  warmGroupMessageEncryption,
} from "./zkCrypto";

export const MAX_GROUP_MEMBERS = 200;
export const MAX_BODY_LENGTH = 4000;
export const MAX_IMAGE_FILE_BYTES = 350 * 1024;
export const MAX_IMAGE_DATA_URL_LENGTH = 525000;
const ZK_ENCRYPTION_ENABLED = true;
export const HOME_SERVER_TAB_ID = "home";
export const ANNOUNCEMENT_CHANNEL_TYPE = "announcement";
export const DEFAULT_CHANNEL_TYPE = "standard";
export const ANNOUNCEMENTS_SERVER_ID = "vm-community";
export const ANNOUNCEMENTS_SERVER_NAME = "VM Community";
export const ANNOUNCEMENTS_CHANNEL_ID = "vm-community-announcements";
export const ANNOUNCEMENTS_CHANNEL_NAME = "Announcements & Rules";
export const MOD_TIMEOUT_LIMIT_PER_DAY = 7;
export const MOD_REMOVE_LIMIT_PER_DAY = 2;
export const HELPER_MOD_TIMEOUT_LIMIT_PER_DAY = 3;
export const HELPER_MOD_REMOVE_LIMIT_PER_DAY = 1;
export const GLOBAL_MENTION_HANDLES = ["everyone", "here"];
// Admin Vivaan is exactly one account: the Firebase Auth login for this email.
// The password is verified by Firebase Auth and is NEVER stored in code.
export const CHAT_ADMIN_EMAILS = [
  "admincommands@gmail.com",
];
// Admin authority is keyed off the verified account email above, not the display
// name, so no login name can grant admin.
export const CHAT_ADMIN_LOGIN_NAMES = [];
export const CHAT_STATUSES = {
  online: { label: "Online", dot: "online" },
  away: { label: "Away", dot: "away" },
  dnd: { label: "Do not disturb", dot: "dnd" },
  invisible: { label: "Invisible", dot: "offline" },
};
const NAME_AUTH_DOMAIN = "vmcreation.chat";
const COMPUTER_LOGIN_NAMES = ["computer"];
const IMAGE_DATA_URL_RE = /^data:image\/[^;]+;base64,/i;
const PUBLIC_KEY_CACHE_BY_UID = new Map();
const LARGE_GROUP_SEND_PREWARM_THRESHOLD = 80;
const SEND_CACHE_WARM_PROMISES = new Map();
const AUTO_KEYSYNC_BACKUP_ID = "account-sync-v1";
const AUTO_KEYSYNC_PASSPHRASE_PREFIX = "__vm_chat_auto_sync__";
const AUTO_KEYSYNC_THROTTLE_MS = 60_000;
const AUTO_KEYSYNC_LAST_PUSH_BY_UID = new Map();
export const MAX_PROFILE_XP_POINTS = 1_000_000_000;
export const XP_REASON_MAX_LENGTH = 140;
export const XP_EVENT_LIST_LIMIT = 20;
export const XP_MAX_ADJUSTMENT_POINTS = 2500;
export const XP_DAILY_ACTION_LIMIT = 40;
export const XP_DAILY_POINTS_LIMIT = 10000;
export const XP_LARGE_CONFIRM_THRESHOLD = 500;
const XP_LEVEL_BASE_STEP = 100;

function clampCounter(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.floor(n);
}

function sanitizeXpPoints(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(MAX_PROFILE_XP_POINTS, Math.floor(n));
}

export function xpPointsForProfile(profile = {}) {
  return sanitizeXpPoints(profile.xpPoints);
}

function sanitizeXpReason(value) {
  return String(value || "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, XP_REASON_MAX_LENGTH);
}

function normalizeXpAdjustmentMode(value) {
  if (value === "grant" || value === "remove" || value === "set") return value;
  return "grant";
}

function sanitizeXpAdminQuota(raw = {}) {
  const dayKey = typeof raw.dayKey === "string" ? raw.dayKey.slice(0, 10) : "";
  return {
    dayKey,
    actionsUsed: clampCounter(raw.actionsUsed),
    pointsUsed: clampCounter(raw.pointsUsed),
  };
}

export function xpAdminQuotaForToday(profile = {}, now = new Date()) {
  const today = moderationDayKey(now);
  const quota = sanitizeXpAdminQuota(profile.xpAdminQuota || {});
  if (quota.dayKey !== today) {
    return { dayKey: today, actionsUsed: 0, pointsUsed: 0 };
  }
  return quota;
}

export function xpThresholdForLevel(level) {
  const safeLevel = Math.max(1, Math.floor(Number(level || 1)));
  return Math.floor(((safeLevel - 1) * safeLevel) / 2) * XP_LEVEL_BASE_STEP;
}

export function xpLevelInfoForPoints(points) {
  const xpPoints = sanitizeXpPoints(points);
  let level = 1;
  while (xpThresholdForLevel(level + 1) <= xpPoints && level < 5000) {
    level += 1;
  }
  const currentLevelXp = xpThresholdForLevel(level);
  const nextLevelXp = xpThresholdForLevel(level + 1);
  const levelSpan = Math.max(1, nextLevelXp - currentLevelXp);
  return {
    xpPoints,
    level,
    currentLevelXp,
    nextLevelXp,
    pointsIntoLevel: xpPoints - currentLevelXp,
    pointsNeeded: Math.max(0, nextLevelXp - xpPoints),
    progress: Math.max(0, Math.min(1, (xpPoints - currentLevelXp) / levelSpan)),
  };
}

export function xpLevelInfoForProfile(profile = {}) {
  return xpLevelInfoForPoints(xpPointsForProfile(profile));
}

export function xpRewardForLevel(level) {
  const safeLevel = Math.max(1, Math.floor(Number(level || 1)));
  if (safeLevel >= 15) return { key: "legend", label: "Legend" };
  if (safeLevel >= 10) return { key: "nova", label: "Nova" };
  if (safeLevel >= 6) return { key: "comet", label: "Comet" };
  if (safeLevel >= 3) return { key: "spark", label: "Spark" };
  return { key: "rookie", label: "Rookie" };
}

export function xpRewardForProfile(profile = {}) {
  const levelInfo = xpLevelInfoForProfile(profile);
  return {
    ...xpRewardForLevel(levelInfo.level),
    levelInfo,
  };
}

export function previewXpAdjustment({ currentPoints = 0, mode = "grant", amount = 0 }) {
  const safeMode = normalizeXpAdjustmentMode(mode);
  const currentXpPoints = sanitizeXpPoints(currentPoints);
  const rawAmount = Number(amount || 0);
  const normalizedAmount = Number.isFinite(rawAmount) ? Math.floor(Math.abs(rawAmount)) : 0;

  if (safeMode === "set") {
    const nextXpPoints = sanitizeXpPoints(rawAmount);
    const delta = nextXpPoints - currentXpPoints;
    return {
      mode: safeMode,
      currentXpPoints,
      nextXpPoints,
      delta,
      absoluteDelta: Math.abs(delta),
    };
  }

  const requestedDelta = safeMode === "remove" ? -normalizedAmount : normalizedAmount;
  const nextXpPoints = sanitizeXpPoints(Math.max(0, currentXpPoints + requestedDelta));
  const delta = nextXpPoints - currentXpPoints;
  return {
    mode: safeMode,
    currentXpPoints,
    nextXpPoints,
    delta,
    absoluteDelta: Math.abs(delta),
  };
}

function parseBooleanLike(value, defaultValue = false) {
  if (value === true || value === "true") return true;
  if (value === false || value === "false") return false;
  return defaultValue;
}

export function moderationDayKey(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function sanitizeModerationQuota(raw = {}) {
  const dayKey = typeof raw.dayKey === "string" ? raw.dayKey.slice(0, 10) : "";
  return {
    dayKey,
    timeoutUsed: clampCounter(raw.timeoutUsed),
    removeUsed: clampCounter(raw.removeUsed),
  };
}

function normalizeModerationRole(value) {
  if (value === "moderator" || value === "helper_mod" || value === "member") return value;
  return "member";
}

export function moderationRoleForProfile(profile = {}) {
  const rawRole = normalizeModerationRole(typeof profile.moderationRole === "string" ? profile.moderationRole : "");
  if (rawRole !== "member") return rawRole;
  if (profile.isModerator) return "moderator";
  return "member";
}

export function moderationRoleLabel(profile = {}) {
  const role = moderationRoleForProfile(profile);
  if (role === "moderator") return "Moderator";
  if (role === "helper_mod") return "Helper mod";
  return "Member";
}

export function moderationQuotaForToday(profile = {}, now = new Date()) {
  const today = moderationDayKey(now);
  const quota = sanitizeModerationQuota(profile.moderationQuota || {});
  if (quota.dayKey !== today) {
    return { dayKey: today, timeoutUsed: 0, removeUsed: 0 };
  }
  return quota;
}

export function isModeratorProfile(profile = {}) {
  const role = moderationRoleForProfile(profile);
  return (role === "moderator" || role === "helper_mod") && profile.moderatorCommandsEnabled !== false;
}

export function isHelperModeratorProfile(profile = {}) {
  return moderationRoleForProfile(profile) === "helper_mod" && profile.moderatorCommandsEnabled !== false;
}

export function canPostAnnouncementProfile(profile = {}) {
  return isChatAdminIdentity(profile) || isModeratorProfile(profile);
}

export function isClownProfile(profile = {}) {
  return !!profile.isClown;
}

export function moderationLimitsForProfile(profile = {}) {
  const role = moderationRoleForProfile(profile);
  if (role === "moderator") {
    return {
      role,
      timeoutLimit: MOD_TIMEOUT_LIMIT_PER_DAY,
      removeLimit: MOD_REMOVE_LIMIT_PER_DAY,
    };
  }
  if (role === "helper_mod") {
    return {
      role,
      timeoutLimit: HELPER_MOD_TIMEOUT_LIMIT_PER_DAY,
      removeLimit: HELPER_MOD_REMOVE_LIMIT_PER_DAY,
    };
  }
  return {
    role: "member",
    timeoutLimit: 0,
    removeLimit: 0,
  };
}

export function canUseModeratorAction(profile = {}, actionType) {
  const quota = moderationQuotaForToday(profile);
  const limits = moderationLimitsForProfile(profile);
  if (actionType === "timeout") return quota.timeoutUsed < limits.timeoutLimit;
  if (actionType === "remove") return quota.removeUsed < limits.removeLimit;
  return false;
}

export function isGlobalMentionHandle(handle = "") {
  const normalized = normalizeLoginName(handle);
  return GLOBAL_MENTION_HANDLES.includes(normalized);
}

function isQuotaExceededError(err) {
  if (!err) return false;
  if (err.code === "resource-exhausted") return true;
  return /quota exceeded|resource exhausted/i.test(String(err.message || ""));
}

function normalizeEmail(value) {
  return String(value || "").trim().toLowerCase();
}

export function normalizeLoginName(name) {
  return String(name || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "")
    .slice(0, 40);
}

export function isChatAdminIdentity(identity = {}) {
  const email = normalizeEmail(identity.email);
  const loginName = normalizeLoginName(identity.loginName || identity.displayName || "");
  return CHAT_ADMIN_EMAILS.includes(email) || CHAT_ADMIN_LOGIN_NAMES.includes(loginName);
}

export function isComputerProfile(profile = {}) {
  const loginName = normalizeLoginName(profile.loginName || "");
  const displayName = normalizeLoginName(profile.displayName || "");
  const email = normalizeEmail(profile.email || "");
  const emailLogin = normalizeLoginName(email.split("@")[0] || "");
  return COMPUTER_LOGIN_NAMES.includes(loginName)
    || COMPUTER_LOGIN_NAMES.includes(displayName)
    || COMPUTER_LOGIN_NAMES.includes(emailLogin);
}

export function isChatAccessDisabledProfile(profile = {}) {
  return parseBooleanLike(profile.chatAccessDisabled, false);
}

export function isProfileTimedOut(profile = {}) {
  return tsMs(profile.timeoutUntil) > Date.now();
}

function authEmailFromName(name) {
  const loginName = normalizeLoginName(name);
  if (loginName.length < 3) {
    throw new Error("Name must have at least 3 letters or numbers.");
  }
  return `${loginName}@${NAME_AUTH_DOMAIN}`;
}

function emailForLogin(nameOrEmail) {
  const raw = String(nameOrEmail || "").trim();
  return raw.includes("@") ? raw.toLowerCase() : authEmailFromName(raw);
}

/**
 * IMPORTANT: Chat profile DOCUMENT IDs ARE the Firebase Auth UIDs — always.
 * Never spread `...snap.data()` *before* `uid` — if the doc accidentally contains a
 * `uid`/`username`/etc. field, it OVERWRITES the real auth uid with garbage → DMs carry
 * the wrong IDs → Firestore rejects `memberIds.contains(request.auth.uid)`.
 *
 * `@param docId` The Firestore document id (truth for auth UID)
 */
export function sanitizeChatProfile(docId, data = {}) {
  let displayName =
    typeof data.displayName === "string" ? data.displayName.trim() : "";
  if (!displayName) displayName = "User";
  displayName = displayName.slice(0, 40);
  const loginName =
    typeof data.loginName === "string" ? data.loginName.trim().slice(0, 40) : "";
  const email = typeof data.email === "string" ? data.email.trim().slice(0, 120) : "";
  const zkPublicKey = typeof data.zkPublicKey === "string" ? data.zkPublicKey.trim() : "";
  const rawStatus = typeof data.status === "string" ? data.status : "online";
  const status = CHAT_STATUSES[rawStatus] ? rawStatus : "online";
  const legacyModerator = parseBooleanLike(data.isModerator, false);
  const moderationRole = normalizeModerationRole(
    typeof data.moderationRole === "string" ? data.moderationRole : (legacyModerator ? "moderator" : "member")
  );
  const isModerator = moderationRole !== "member";
  const moderatorCommandsEnabled = parseBooleanLike(data.moderatorCommandsEnabled, true);
  const moderationQuota = sanitizeModerationQuota(data.moderationQuota || {});
  const xpAdminQuota = sanitizeXpAdminQuota(data.xpAdminQuota || {});
  const isClown = parseBooleanLike(data.isClown, false);
  const chatAccessDisabled = parseBooleanLike(data.chatAccessDisabled, false);
  const xpPoints = sanitizeXpPoints(data.xpPoints);
  return {
    uid: docId,
    displayName,
    loginName,
    email,
    zkPublicKey,
    status,
    lastActive: data.lastActive,
    timeoutUntil: data.timeoutUntil || null,
    timedOutBy: typeof data.timedOutBy === "string" ? data.timedOutBy : "",
    isModerator,
    moderationRole,
    moderatorCommandsEnabled: isModerator && moderatorCommandsEnabled,
    moderatorAssignedBy: typeof data.moderatorAssignedBy === "string" ? data.moderatorAssignedBy : "",
    moderatorAssignedAt: data.moderatorAssignedAt || null,
    modCongratsUntil: data.modCongratsUntil || null,
    isClown,
    clownAssignedBy: typeof data.clownAssignedBy === "string" ? data.clownAssignedBy : "",
    clownAssignedAt: data.clownAssignedAt || null,
    moderationQuota,
    xpAdminQuota,
    xpPoints,
    chatAccessDisabled,
    chatAccessDisabledBy: typeof data.chatAccessDisabledBy === "string" ? data.chatAccessDisabledBy : "",
    chatAccessDisabledAt: data.chatAccessDisabledAt || null,
  };
}

export function sanitizeXpEvent(docId, data = {}) {
  const rawAmount = Number(data.amount || 0);
  const rawAbsoluteAmount = Number(data.absoluteAmount || Math.abs(rawAmount));
  const rawPrevious = Number(data.previousXpPoints || 0);
  const rawNext = Number(data.nextXpPoints || 0);
  return {
    id: docId,
    type: normalizeXpAdjustmentMode(typeof data.type === "string" ? data.type : "grant"),
    amount: Number.isFinite(rawAmount) ? Math.floor(rawAmount) : 0,
    absoluteAmount: Number.isFinite(rawAbsoluteAmount) ? Math.floor(Math.abs(rawAbsoluteAmount)) : 0,
    previousXpPoints: sanitizeXpPoints(rawPrevious),
    nextXpPoints: sanitizeXpPoints(rawNext),
    givenBy: typeof data.givenBy === "string" ? data.givenBy : "",
    givenByName: typeof data.givenByName === "string" ? data.givenByName.slice(0, 40) : "",
    reason: sanitizeXpReason(data.reason),
    createdAt: data.createdAt || null,
  };
}

export function describeXpEvent(event = {}, profilesByUid = {}) {
  const giverName = profilesByUid[event.givenBy]?.displayName || event.givenByName || "Admin Vivaan";
  const delta = Number(event.amount || 0);
  const absDelta = Math.abs(delta);
  const reason = sanitizeXpReason(event.reason);
  const reasonSuffix = reason ? `: ${reason}` : "";

  if (event.type === "set") {
    return `${giverName} set your XP to ${sanitizeXpPoints(event.nextXpPoints)}${reasonSuffix}`;
  }
  if (delta < 0) {
    return `${giverName} removed ${absDelta} XP${reasonSuffix}`;
  }
  return `${giverName} gave you ${absDelta} XP${reasonSuffix}`;
}

/** Normalize a conversation document so UI code never throws on missing/invalid `memberIds`. */
export function sanitizeConversation(docId, data = {}) {
  const raw = data.memberIds;
  const memberIds = Array.isArray(raw)
    ? raw.filter((id) => typeof id === "string" && id.length > 0)
    : [];
  const type =
    data.type === "group" || data.type === "dm"
      ? data.type
      : memberIds.length > 2
        ? "group"
        : "dm";
  const name = typeof data.name === "string" ? data.name.slice(0, 60) : null;
    const serverId = typeof data.serverId === "string" ? data.serverId.slice(0, 80) : "";
    const serverName = typeof data.serverName === "string" ? data.serverName.slice(0, 60) : "";
    const channelType = data.channelType === ANNOUNCEMENT_CHANNEL_TYPE
      ? ANNOUNCEMENT_CHANNEL_TYPE
      : DEFAULT_CHANNEL_TYPE;
    const channelOrder = Number.isFinite(Number(data.channelOrder))
      ? Number(data.channelOrder)
      : 0;
  const lastMessage =
    data.lastMessage && typeof data.lastMessage === "object"
      ? {
          body:
            typeof data.lastMessage.body === "string"
              ? data.lastMessage.body
              : "",
          senderId:
            typeof data.lastMessage.senderId === "string"
              ? data.lastMessage.senderId
              : "",
          createdAt: data.lastMessage.createdAt,
        }
      : null;
  return {
    id: docId,
    type,
    name,
    memberIds,
    createdBy: typeof data.createdBy === "string" ? data.createdBy : null,
    createdAt: data.createdAt,
    serverId,
    serverName,
    channelType,
    channelOrder,
    lastMessage,
  };
}

export function isAnnouncementChannelConversation(conversation = {}) {
  return conversation.channelType === ANNOUNCEMENT_CHANNEL_TYPE;
}

export function sanitizeConversationPref(docId, data = {}) {
  return {
    convId: docId,
    hidden: !!data.hidden,
    archived: !!data.archived,
    lastReadAt: data.lastReadAt || null,
    updatedAt: data.updatedAt || null,
  };
}

export function messageMentionsProfile(message = {}, profile = {}) {
  const body = String(message.body || "");
  if (/(^|\s)@(everyone|here)(?=\b)/i.test(body)) return true;
  const handle = normalizeLoginName(profile.loginName || profile.displayName || "");
  if (!handle) return false;
  return new RegExp(`(^|\\s)@${handle}(?=\\b)`, "i").test(body);
}

function isValidImageDataUrl(value) {
  return typeof value === "string"
    && value.length > 30
    && value.length <= MAX_IMAGE_DATA_URL_LENGTH
    && IMAGE_DATA_URL_RE.test(value);
}

function sanitizeSenderKeyBoxes(rawBoxes) {
  if (!rawBoxes || typeof rawBoxes !== "object" || Array.isArray(rawBoxes)) return {};
  const out = {};
  Object.entries(rawBoxes).forEach(([uid, packet]) => {
    if (!uid || typeof packet !== "object" || Array.isArray(packet)) return;
    if (typeof packet.iv !== "string" || typeof packet.ciphertext !== "string") return;
    out[uid] = {
      iv: packet.iv,
      ciphertext: packet.ciphertext,
    };
  });
  return out;
}

function sanitizeMessageEncryption(enc) {
  if (!enc || typeof enc !== "object" || Array.isArray(enc)) return null;
  const hasSenderKeyBoxes = !!(enc.senderKeyBoxes && typeof enc.senderKeyBoxes === "object");
  const hasSenderKeyVersion = enc.senderKeyVersion !== undefined && enc.senderKeyVersion !== null;
  const kind = enc.kind === "group"
    ? "group"
    : enc.kind === "dm"
      ? "dm"
      : (hasSenderKeyBoxes || hasSenderKeyVersion ? "group" : "");
  const v = Number(enc.v || 1);
  const senderKeyVersion = Number(enc.senderKeyVersion || 1);
  return {
    v: Number.isFinite(v) && v > 0 ? v : 1,
    alg: typeof enc.alg === "string" ? enc.alg : "AES-GCM",
    kind,
    senderUid: typeof enc.senderUid === "string" ? enc.senderUid : "",
    senderPub: typeof enc.senderPub === "string" ? enc.senderPub : "",
    recipientPub: typeof enc.recipientPub === "string" ? enc.recipientPub : "",
    iv: typeof enc.iv === "string" ? enc.iv : "",
    senderKeyVersion: Number.isFinite(senderKeyVersion) && senderKeyVersion > 0
      ? senderKeyVersion
      : 1,
    senderKeyBoxes: sanitizeSenderKeyBoxes(enc.senderKeyBoxes),
  };
}

/** Normalize a message doc for rendering; avoids `.find`/undefined `senderId` crashes. */
export function sanitizeMessage(docId, data = {}) {
  const roomMembers = Array.isArray(data.roomMembers)
    ? data.roomMembers.filter((id) => typeof id === "string" && id.length > 0)
    : [];
  const hiddenFor = Array.isArray(data.hiddenFor)
    ? data.hiddenFor.filter((id) => typeof id === "string" && id.length > 0)
    : [];
  const reactions =
    data.reactions && typeof data.reactions === "object" && !Array.isArray(data.reactions)
      ? data.reactions
      : {};
  const replyTo =
    data.replyTo && typeof data.replyTo === "object"
      ? {
          id: typeof data.replyTo.id === "string" ? data.replyTo.id : "",
          body: typeof data.replyTo.body === "string" ? data.replyTo.body : "",
          senderId: typeof data.replyTo.senderId === "string" ? data.replyTo.senderId : "",
        }
      : null;
  const forwardedFrom =
    data.forwardedFrom && typeof data.forwardedFrom === "object"
      ? {
          senderId: typeof data.forwardedFrom.senderId === "string" ? data.forwardedFrom.senderId : "",
          conversationId:
            typeof data.forwardedFrom.conversationId === "string"
              ? data.forwardedFrom.conversationId
              : "",
          conversationName:
            typeof data.forwardedFrom.conversationName === "string"
              ? data.forwardedFrom.conversationName.slice(0, 80)
              : "",
        }
      : null;
  const imageDataUrl = isValidImageDataUrl(data.imageDataUrl) ? data.imageDataUrl : "";
  const enc = sanitizeMessageEncryption(data.enc);
  return {
    id: docId,
    senderId: typeof data.senderId === "string" ? data.senderId : "",
    body: typeof data.body === "string" ? data.body : "",
    imageDataUrl,
    createdAt: data.createdAt,
    editedAt: data.editedAt,
    deletedForEveryoneAt: data.deletedForEveryoneAt,
    deletedBy: typeof data.deletedBy === "string" ? data.deletedBy : "",
    hiddenFor,
    roomMembers,
    reactions,
    replyTo,
    forwardedFrom,
    enc,
  };
}

export function describeMessagePreview(message = {}) {
  if (message.deletedForEveryoneAt) return "Message deleted";
  const body = typeof message.body === "string" ? message.body.trim() : "";
  if (/^encrypted reply$/i.test(body)) return "Original message unavailable";
  if (/^\[unable to decrypt message\]$/i.test(body)) return "Original message unavailable";
  if (!body && isValidImageDataUrl(message.imageDataUrl)) return "Photo";
  return body;
}

export function isMessageDeleted(message = {}) {
  return !!message.deletedForEveryoneAt;
}

// ── Auth ───────────────────────────────────────────────────────────────
export async function signUp({ name, email, password, displayName }) {
  const finalDisplayName = (displayName || name || "").trim().slice(0, 40);
  const loginName = normalizeLoginName(name || displayName || email);
  const authEmail = email ? email.trim().toLowerCase() : authEmailFromName(name || displayName);
  const cred = await createUserWithEmailAndPassword(auth, authEmail, password);
  const identity = await ensureIdentityKeyPair({ uid: cred.user.uid });
  // Mirror the public-ish profile so other users can discover this account in the picker.
  await setDoc(doc(db, "chatProfiles", cred.user.uid), {
    displayName: finalDisplayName || loginName || "User",
    loginName,
    email: email ? email.trim().toLowerCase().slice(0, 120) : "",
    zkPublicKey: identity.publicKeyBase64,
    isNameLogin: !email,
    status: "online",
    lastActive: serverTimestamp(),
    createdAt: serverTimestamp(),
  });
  return cred.user;
}

export function signIn({ name, email, password }) {
  return signInWithEmailAndPassword(auth, email ? email.trim().toLowerCase() : emailForLogin(name), password);
}

export function signOutChat() {
  return signOut(auth);
}

function autoKeySyncPassphrase(uid) {
  return `${AUTO_KEYSYNC_PASSPHRASE_PREFIX}:${String(uid || "")}`;
}

function autoKeySyncRef(uid) {
  return doc(db, "chatProfiles", uid, "zkBackups", AUTO_KEYSYNC_BACKUP_ID);
}

async function recoverAutoSyncedKeys(uid) {
  const ref = autoKeySyncRef(uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    return { exists: false, recovered: false };
  }
  await recoverLocalKeys({
    uid,
    passphrase: autoKeySyncPassphrase(uid),
    backupPayload: snap.data(),
  });
  return { exists: true, recovered: true };
}

async function pushAutoSyncedKeys(uid, { forceUpload = false } = {}) {
  const now = Date.now();
  const last = Number(AUTO_KEYSYNC_LAST_PUSH_BY_UID.get(uid) || 0);
  if (!forceUpload && now - last < AUTO_KEYSYNC_THROTTLE_MS) {
    return { uploaded: false, throttled: true };
  }

  let profileKey = "";
  try {
    const profileSnap = await getDoc(doc(db, "chatProfiles", uid));
    if (profileSnap.exists()) {
      const profile = sanitizeChatProfile(profileSnap.id, profileSnap.data());
      profileKey = profile.zkPublicKey || "";
    }
  } catch {
    profileKey = "";
  }

  if (profileKey) {
    try {
      const identity = await ensureIdentityKeyPair({
        uid,
        existingPublicKeyBase64: profileKey,
      });
      if (identity.publicKeyBase64 && identity.publicKeyBase64 !== profileKey) {
        throw new Error("Local key mismatch with profile key.");
      }
    } catch {
      throw new Error("Skipped auto key upload because local/profile keys do not match.");
    }
  }

  const backup = await backupLocalKeys({
    uid,
    passphrase: autoKeySyncPassphrase(uid),
    includeSenderKeys: false,
  });
  await setDoc(
    autoKeySyncRef(uid),
    {
      ...backup,
      mode: "account-auto-sync",
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
  AUTO_KEYSYNC_LAST_PUSH_BY_UID.set(uid, now);
  return { uploaded: true, throttled: false };
}

export async function syncChatKeysAcrossDevices({
  uid,
  recover = true,
  upload = true,
  forceUpload = false,
} = {}) {
  if (!uid) {
    return {
      cloudBackupExists: false,
      recovered: false,
      uploaded: false,
      throttled: false,
      recoverError: null,
      uploadError: null,
    };
  }

  let cloudBackupExists = false;
  let recovered = false;
  let uploaded = false;
  let throttled = false;
  let recoverError = null;
  let uploadError = null;

  if (recover) {
    try {
      const recoverResult = await recoverAutoSyncedKeys(uid);
      cloudBackupExists = !!recoverResult.exists;
      recovered = !!recoverResult.recovered;
    } catch (err) {
      recoverError = err;
    }
  }

  if (upload) {
    try {
      const uploadResult = await pushAutoSyncedKeys(uid, { forceUpload });
      uploaded = !!uploadResult.uploaded;
      throttled = !!uploadResult.throttled;
    } catch (err) {
      uploadError = err;
    }
  }

  return {
    cloudBackupExists,
    recovered,
    uploaded,
    throttled,
    recoverError,
    uploadError,
  };
}

// ── Profile ────────────────────────────────────────────────────────────
export async function ensureProfile(user, fallbackDisplayName) {
  // Self-heal: if a user signed in via the existing site auth before chat existed — make profile.
  const ref = doc(db, "chatProfiles", user.uid);
  const snap = await getDoc(ref);
  const existingData = snap.exists() ? snap.data() : null;

  // Keep Admin Vivaan's profile self-identifying. Admin write authority comes from the
  // auth token (isChatAdminIdentity below / isAdminPrincipal in rules), but the rules that
  // PROTECT the admin's moderation actions from being undone by moderators
  // (isAdminProfile) can only read the profile document. If the admin signed in with a
  // Google account, their profile may lack an admin email/loginName, so backfill it here.
  // The Firestore spoof-guard only lets the real admin hold these values.
  try {
    if (snap.exists()) {
      const authEmail = (user.email || "").trim().toLowerCase();
      const profileEmail = (existingData?.email || "").trim().toLowerCase();
      const profileLoginName = existingData?.loginName || "";
      const adminByToken = isChatAdminIdentity({ email: authEmail, loginName: profileLoginName });
      const profileSelfIdentifiesAsAdmin = isChatAdminIdentity({ email: profileEmail, loginName: profileLoginName });
      if (adminByToken && !profileSelfIdentifiesAsAdmin && authEmail) {
        await updateDoc(ref, { email: authEmail }).catch(() => {});
      }
    }
  } catch (_) {}

  // Recover account-bound keys before any local key generation to avoid unwanted key rotation
  // on a new browser/device when the account already has synced keys.
  await syncChatKeysAcrossDevices({
    uid: user.uid,
    recover: true,
    upload: false,
  }).catch(() => {});

  const identity = await ensureIdentityKeyPair({
    uid: user.uid,
    existingPublicKeyBase64: existingData?.zkPublicKey || "",
  });

  if (snap.exists()) {
    const profile = sanitizeChatProfile(user.uid, existingData);
    const profileKey = profile.zkPublicKey || "";
    const identityKey = identity.publicKeyBase64 || "";

    if (profileKey && identityKey && identityKey !== profileKey) {
      // Do not overwrite existing profile key automatically on mismatch.
      // Try one more recover pass, then keep profile as source of truth.
      await syncChatKeysAcrossDevices({
        uid: user.uid,
        recover: true,
        upload: false,
      }).catch(() => {});

      let recoveredIdentity = null;
      try {
        recoveredIdentity = await ensureIdentityKeyPair({
          uid: user.uid,
          existingPublicKeyBase64: profileKey,
        });
      } catch {
        recoveredIdentity = null;
      }

      const recoveredKey = recoveredIdentity?.publicKeyBase64 || "";
      if (!recoveredKey || recoveredKey !== profileKey) {
        return profile;
      }
    }

    if (identity.needsProfilePublish && !profileKey) {
      await updateDoc(ref, {
        zkPublicKey: identity.publicKeyBase64,
      }).catch(() => {});
      await syncChatKeysAcrossDevices({
        uid: user.uid,
        recover: false,
        upload: true,
        forceUpload: true,
      }).catch(() => {});
      return {
        ...profile,
        zkPublicKey: identity.publicKeyBase64,
      };
    }

    await syncChatKeysAcrossDevices({
      uid: user.uid,
      recover: false,
      upload: true,
      forceUpload: true,
    }).catch(() => {});
    return profile;
  }

  const displayName = (fallbackDisplayName || user.email?.split("@")[0] || "User").slice(0, 40);
  const authEmailForProfile = (user.email || "").trim().toLowerCase().slice(0, 120);
  await setDoc(ref, {
    displayName,
    email: authEmailForProfile,
    zkPublicKey: identity.publicKeyBase64,
    status: "online",
    lastActive: serverTimestamp(),
    createdAt: serverTimestamp(),
  });

  await syncChatKeysAcrossDevices({
    uid: user.uid,
    recover: false,
    upload: true,
    forceUpload: true,
  }).catch(() => {});

  return {
    uid: user.uid,
    displayName,
    email: user.email || "",
    zkPublicKey: identity.publicKeyBase64,
    status: "online",
    xpPoints: 0,
  };
}

export async function listOtherProfiles(myUid) {
  // 100 users → tiny payload, fine to fetch all and exclude self in memory.
  const snap = await getDocs(collection(db, "chatProfiles"));
  const out = [];
  snap.forEach((d) => {
    if (d.id === myUid) return;
    const profile = sanitizeChatProfile(d.id, d.data());
    if (profile.chatAccessDisabled) return;
    out.push(profile);
  });
  out.sort((a, b) => (a.displayName || "").localeCompare(b.displayName || ""));
  return out;
}

export function subscribeConversationPrefs(myUid, cb, onError) {
  if (!myUid || typeof myUid !== "string") {
    console.warn("[chat] subscribeConversationPrefs skipped: invalid uid");
    return () => {};
  }
  return onSnapshot(
    collection(db, "chatProfiles", myUid, "conversationPrefs"),
    (snap) => {
      const out = {};
      snap.forEach((d) => {
        out[d.id] = sanitizeConversationPref(d.id, d.data());
      });
      cb(out);
    },
    (err) => {
      try { err.where = "conversation-prefs"; err.uidUsed = myUid; } catch (_) {}
      console.error("[chat] conversation prefs listener error:", err);
      onError && onError(err);
    }
  );
}

async function updateConversationPref(myUid, convId, patch) {
  if (!myUid || !convId) return;
  await setDoc(
    doc(db, "chatProfiles", myUid, "conversationPrefs", convId),
    {
      ...patch,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

export function setConversationHidden({ myUid, convId, hidden }) {
  return updateConversationPref(myUid, convId, { hidden: !!hidden });
}

export function setConversationArchived({ myUid, convId, archived }) {
  return updateConversationPref(myUid, convId, { archived: !!archived });
}

export function setConversationLastRead({ myUid, convId, lastReadAt }) {
  return updateConversationPref(myUid, convId, { lastReadAt: lastReadAt || serverTimestamp() });
}

export function subscribeChatProfiles(cb, onError) {
  return onSnapshot(
    collection(db, "chatProfiles"),
    (snap) => {
      const out = {};
      snap.forEach((d) => {
        const profile = sanitizeChatProfile(d.id, d.data());
        out[d.id] = profile;
        if (profile.zkPublicKey) {
          PUBLIC_KEY_CACHE_BY_UID.set(d.id, profile.zkPublicKey);
        }
      });
      cb(out);
    },
    (err) => {
      try { err.where = "chat-profiles"; } catch (_) {}
      onError && onError(err);
    }
  );
}

export function subscribeXpEvents(uid, cb, onError, limitCount = XP_EVENT_LIST_LIMIT) {
  if (!uid) {
    if (typeof cb === "function") cb([]);
    return () => {};
  }

  return onSnapshot(
    query(
      collection(db, "chatProfiles", uid, "xpEvents"),
      orderBy("createdAt", "desc"),
      fbLimit(Math.max(1, limitCount)),
    ),
    (snap) => {
      const out = [];
      snap.forEach((row) => {
        out.push(sanitizeXpEvent(row.id, row.data()));
      });
      cb(out);
    },
    (err) => {
      try { err.where = "xp-events"; err.uidUsed = uid; } catch (_) {}
      onError && onError(err);
    }
  );
}

export async function backupChatKeysToCloud({ uid, passphrase }) {
  if (!uid) throw new Error("You need to be signed in.");
  const backup = await backupLocalKeys({ uid, passphrase });
  await setDoc(
    doc(db, "chatProfiles", uid, "zkBackups", "default"),
    {
      ...backup,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
  return backup;
}

export async function recoverChatKeysFromCloud({ uid, passphrase }) {
  if (!uid) throw new Error("You need to be signed in.");
  const snap = await getDoc(doc(db, "chatProfiles", uid, "zkBackups", "default"));
  if (!snap.exists()) {
    throw new Error("No cloud backup found for this account yet.");
  }
  return recoverLocalKeys({
    uid,
    passphrase,
    backupPayload: snap.data(),
  });
}

export function subscribeCloudKeyBackupStatus(uid, cb, onError) {
  if (!uid) {
    if (typeof cb === "function") {
      cb({ exists: false, updatedAt: null, createdAt: null });
    }
    return () => {};
  }
  return onSnapshot(
    doc(db, "chatProfiles", uid, "zkBackups", "default"),
    (snap) => {
      const data = snap.exists() ? (snap.data() || {}) : {};
      const createdAt = Number(data.createdAt);
      if (typeof cb === "function") {
        cb({
          exists: snap.exists(),
          updatedAt: data.updatedAt || null,
          createdAt: Number.isFinite(createdAt) ? createdAt : null,
        });
      }
    },
    (err) => {
      try { err.where = "zk-backup-status"; err.uidUsed = uid; } catch (_) {}
      onError && onError(err);
    }
  );
}

export async function setUserStatus(uid, status) {
  if (!uid || !CHAT_STATUSES[status]) return;
  await updateDoc(doc(db, "chatProfiles", uid), {
    status,
    lastActive: serverTimestamp(),
  });
}

export async function setChatTimeout({ targetUid, durationMs, myUid }) {
  if (!targetUid || !myUid || targetUid === myUid) {
    throw new Error("Pick another user to timeout.");
  }
  const targetSnap = await getDoc(doc(db, "chatProfiles", targetUid));
  if (targetSnap.exists()) {
    const targetProfile = sanitizeChatProfile(targetSnap.id, targetSnap.data());
    if (isChatAdminIdentity(targetProfile)) {
      throw new Error("Admin Vivaan cannot be timed out.");
    }
  }
  const ms = Number(durationMs || 0);
  if (!Number.isFinite(ms) || ms <= 0) {
    throw new Error("Timeout must be longer than zero.");
  }
  await updateDoc(doc(db, "chatProfiles", targetUid), {
    timeoutUntil: Timestamp.fromMillis(Date.now() + ms),
    timedOutBy: myUid,
  });
}

export async function clearChatTimeout({ targetUid }) {
  if (!targetUid) return;
  await updateDoc(doc(db, "chatProfiles", targetUid), {
    timeoutUntil: null,
    timedOutBy: null,
  });
}

export async function consumeModeratorActionQuota({ uid, profile, actionType }) {
  if (!uid) return moderationQuotaForToday(profile);
  const quota = moderationQuotaForToday(profile);
  const limits = moderationLimitsForProfile(profile);
  const next = {
    dayKey: quota.dayKey,
    timeoutUsed: quota.timeoutUsed,
    removeUsed: quota.removeUsed,
  };
  if (actionType === "timeout") {
    if (next.timeoutUsed >= limits.timeoutLimit) {
      throw new Error("Daily timeout limit reached for your role.");
    }
    next.timeoutUsed += 1;
  } else if (actionType === "remove") {
    if (next.removeUsed >= limits.removeLimit) {
      throw new Error("Daily remove limit reached for your role.");
    }
    next.removeUsed += 1;
  } else {
    return quota;
  }
  await updateDoc(doc(db, "chatProfiles", uid), {
    moderationQuota: next,
  });
  return next;
}

export async function setMemberModerationRole({ targetUid, moderationRole = "member", myUid }) {
  if (!targetUid || !myUid) {
    throw new Error("Moderator action needs both users.");
  }
  if (targetUid === myUid) {
    throw new Error("Use a different account when assigning moderator roles.");
  }
  const role = normalizeModerationRole(moderationRole);
  const turnsOn = role !== "member";

  await updateDoc(doc(db, "chatProfiles", targetUid), {
    isModerator: turnsOn,
    moderationRole: role,
    moderatorCommandsEnabled: turnsOn,
    moderatorAssignedBy: myUid,
    moderatorAssignedAt: serverTimestamp(),
    modCongratsUntil: turnsOn ? Timestamp.fromMillis(Date.now() + 2000) : null,
    moderationQuota: {
      dayKey: moderationDayKey(),
      timeoutUsed: 0,
      removeUsed: 0,
    },
  });
}

export async function setClownRole({ targetUid, enabled, myUid }) {
  if (!targetUid || !myUid) {
    throw new Error("Clown role action needs both users.");
  }
  if (targetUid === myUid) {
    throw new Error("Use a different account when assigning clown roles.");
  }
  await updateDoc(doc(db, "chatProfiles", targetUid), {
    isClown: !!enabled,
    clownAssignedBy: myUid,
    clownAssignedAt: enabled ? serverTimestamp() : null,
  });
}

export async function adjustXpPoints({
  targetUid,
  amount,
  myUid,
  mode = "grant",
  reason = "",
}) {
  if (!targetUid || !myUid) {
    throw new Error("XP action needs both users.");
  }
  if (targetUid === myUid) {
    throw new Error("Admin Vivaan cannot award XP to the same account.");
  }

  const safeMode = normalizeXpAdjustmentMode(mode);
  const safeReason = sanitizeXpReason(reason);
  if (safeReason.length < 3) {
    throw new Error("Give a short reason for the XP change.");
  }

  return runTransaction(db, async (transaction) => {
    const actorRef = doc(db, "chatProfiles", myUid);
    const targetRef = doc(db, "chatProfiles", targetUid);
    const actorSnap = await transaction.get(actorRef);
    const targetSnap = await transaction.get(targetRef);

    if (!actorSnap.exists()) {
      throw new Error("Your admin profile is missing.");
    }
    if (!targetSnap.exists()) {
      throw new Error("That user no longer exists.");
    }

    const actorProfile = sanitizeChatProfile(actorSnap.id, actorSnap.data());
    const actorIdentity = {
      ...actorProfile,
      email: actorProfile.email || auth.currentUser?.email || "",
    };
    if (!isChatAdminIdentity(actorIdentity)) {
      throw new Error("Only Admin Vivaan can change XP points.");
    }

    const targetProfile = sanitizeChatProfile(targetSnap.id, targetSnap.data());
    const preview = previewXpAdjustment({
      currentPoints: xpPointsForProfile(targetProfile),
      mode: safeMode,
      amount,
    });

    if (!preview.absoluteDelta) {
      throw new Error("That XP action would not change anything.");
    }
    if (preview.absoluteDelta > XP_MAX_ADJUSTMENT_POINTS) {
      throw new Error(`You can change at most ${XP_MAX_ADJUSTMENT_POINTS} XP in one action.`);
    }
    if (preview.nextXpPoints > MAX_PROFILE_XP_POINTS) {
      throw new Error("That XP total is too large.");
    }

    const quota = xpAdminQuotaForToday(actorProfile);
    if (quota.actionsUsed + 1 > XP_DAILY_ACTION_LIMIT) {
      throw new Error(`Daily XP admin limit reached (${XP_DAILY_ACTION_LIMIT} actions).`);
    }
    if (quota.pointsUsed + preview.absoluteDelta > XP_DAILY_POINTS_LIMIT) {
      throw new Error(`Daily XP admin limit reached (${XP_DAILY_POINTS_LIMIT} points).`);
    }

    transaction.update(actorRef, {
      xpAdminQuota: {
        dayKey: quota.dayKey,
        actionsUsed: quota.actionsUsed + 1,
        pointsUsed: quota.pointsUsed + preview.absoluteDelta,
      },
    });
    transaction.update(targetRef, {
      xpPoints: preview.nextXpPoints,
    });

    const eventRef = doc(collection(db, "chatProfiles", targetUid, "xpEvents"));
    transaction.set(eventRef, {
      type: safeMode,
      amount: preview.delta,
      absoluteAmount: preview.absoluteDelta,
      previousXpPoints: preview.currentXpPoints,
      nextXpPoints: preview.nextXpPoints,
      givenBy: myUid,
      givenByName: actorProfile.displayName || "Admin Vivaan",
      reason: safeReason,
      createdAt: serverTimestamp(),
    });

    return {
      targetUid,
      targetDisplayName: targetProfile.displayName || "User",
      mode: safeMode,
      delta: preview.delta,
      nextXpPoints: preview.nextXpPoints,
      quota: {
        dayKey: quota.dayKey,
        actionsUsed: quota.actionsUsed + 1,
        pointsUsed: quota.pointsUsed + preview.absoluteDelta,
      },
    };
  });
}

export async function grantXpPoints({ targetUid, amount, myUid }) {
  return adjustXpPoints({
    targetUid,
    amount,
    myUid,
    mode: "grant",
    reason: "Manual XP grant",
  });
}

// Ban (disabled=true) or unban (disabled=false) ANY non-admin account. A ban sets
// chatProfiles.chatAccessDisabled, which the Firestore rules use to block ALL writes
// from that account (messages, edits, reactions, typing, conversation creation), and
// which the client uses to hide every message the banned person ever sent.
// Admin Vivaan only. Admin accounts can never be banned.
export async function setChatAccessDisabled({ targetUid, disabled = true, myUid }) {
  if (!targetUid || !myUid) {
    throw new Error("Chat access action needs both users.");
  }
  if (targetUid === myUid) {
    throw new Error("Admin account cannot ban itself.");
  }

  const actorSnap = await getDoc(doc(db, "chatProfiles", myUid));
  const actorProfile = actorSnap.exists()
    ? sanitizeChatProfile(actorSnap.id, actorSnap.data())
    : { uid: myUid };
  const actorIdentity = {
    ...actorProfile,
    email: actorProfile.email || auth.currentUser?.email || "",
  };
  if (!isChatAdminIdentity(actorIdentity)) {
    throw new Error("Only Admin Vivaan can ban or unban people.");
  }

  const targetRef = doc(db, "chatProfiles", targetUid);
  const targetSnap = await getDoc(targetRef);
  if (!targetSnap.exists()) {
    throw new Error("That account no longer exists.");
  }
  const targetProfile = sanitizeChatProfile(targetSnap.id, targetSnap.data());
  if (isChatAdminIdentity(targetProfile)) {
    throw new Error("Admin Vivaan cannot be banned.");
  }

  const nextDisabled = !!disabled;
  await updateDoc(
    targetRef,
    nextDisabled
      ? {
          chatAccessDisabled: true,
          chatAccessDisabledBy: myUid,
          chatAccessDisabledAt: serverTimestamp(),
        }
      : {
          chatAccessDisabled: false,
          chatAccessDisabledBy: null,
          chatAccessDisabledAt: null,
        }
  );

  // When banning, kick the person out of every group so they stop receiving new
  // messages. Best-effort: a single denied or legacy conversation update must NOT
  // abort the ban — the chatAccessDisabled flag above is the authoritative block.
  let groupsUpdated = 0;
  if (nextDisabled) {
    try {
      const convSnap = await getDocs(
        query(collection(db, "conversations"), where("memberIds", "array-contains", targetUid))
      );
      for (const row of convSnap.docs) {
        try {
          const conversation = sanitizeConversation(row.id, row.data());
          if (conversation.type !== "group") continue;
          if (!conversation.memberIds.includes(targetUid)) continue;
          const nextMemberIds = conversation.memberIds.filter((uid) => uid !== targetUid);
          if (nextMemberIds.length === 0) continue;

          await updateDoc(doc(db, "conversations", conversation.id), {
            memberIds: nextMemberIds,
            createdBy: conversation.createdBy === targetUid
              ? nextMemberIds[0]
              : conversation.createdBy,
          });
          groupsUpdated += 1;
        } catch (stepErr) {
          console.warn("[chat] setChatAccessDisabled: group kick step failed", stepErr);
        }
      }
    } catch (scanErr) {
      console.warn("[chat] setChatAccessDisabled: group scan failed", scanErr);
    }
  }

  return {
    disabled: nextDisabled,
    groupsUpdated,
  };
}

// Admin-only nuclear option: completely remove the Computer account from the
// website. Steps:
//   1. Verify the actor is Admin Vivaan and the target is the Computer profile.
//   2. Kick Computer out of every group conversation it belongs to.
//   3. Write a tombstone at `chatBans/{targetUid}` so the profile cannot be
//      re-created on the next sign-in (firestore.rules denies create when a
//      matching ban doc exists).
//   4. Delete `chatProfiles/{targetUid}` outright so the account vanishes from
//      member lists, sidebars, and moderator panels.
// Unlike `setChatAccessDisabled`, this can be invoked WHENEVER Admin Vivaan
// wants — even repeatedly if Computer ever returns.
export async function removeComputerFromWebsite({ targetUid, myUid }) {
  if (!targetUid || !myUid) {
    throw new Error("Removal action needs both users.");
  }
  if (targetUid === myUid) {
    throw new Error("Admin account cannot remove itself.");
  }

  const actorSnap = await getDoc(doc(db, "chatProfiles", myUid));
  const actorProfile = actorSnap.exists()
    ? sanitizeChatProfile(actorSnap.id, actorSnap.data())
    : { uid: myUid };
  const actorIdentity = {
    ...actorProfile,
    email: actorProfile.email || auth.currentUser?.email || "",
  };
  if (!isChatAdminIdentity(actorIdentity)) {
    throw new Error("Only Admin Vivaan can do that.");
  }

  const targetRef = doc(db, "chatProfiles", targetUid);
  const targetSnap = await getDoc(targetRef);
  const targetProfile = targetSnap.exists()
    ? sanitizeChatProfile(targetSnap.id, targetSnap.data())
    : null;
  if (targetProfile && isChatAdminIdentity(targetProfile)) {
    throw new Error("Admin Vivaan cannot be removed from the website.");
  }
  if (targetProfile && !isComputerProfile(targetProfile)) {
    throw new Error("This permanent removal is reserved for the physical device account.");
  }

  // 1. Kick Computer from every group conversation it currently belongs to.
  let groupsUpdated = 0;
  try {
    const convSnap = await getDocs(
      query(collection(db, "conversations"), where("memberIds", "array-contains", targetUid))
    );
    for (const row of convSnap.docs) {
      const conversation = sanitizeConversation(row.id, row.data());
      if (conversation.type !== "group") continue;
      if (!conversation.memberIds.includes(targetUid)) continue;
      const nextMemberIds = conversation.memberIds.filter((uid) => uid !== targetUid);
      if (nextMemberIds.length === 0) continue;
      await updateDoc(doc(db, "conversations", conversation.id), {
        memberIds: nextMemberIds,
        createdBy: conversation.createdBy === targetUid
          ? nextMemberIds[0]
          : conversation.createdBy,
      });
      groupsUpdated += 1;
    }
  } catch (e) {
    // Best effort: rules may deny if the listener has stale data. Don't block
    // the overall removal — the profile delete + ban tombstone are the
    // authoritative steps.
    console.warn("[chat] removeComputerFromWebsite: kick step failed", e);
  }

  // 2. Write the ban tombstone BEFORE deleting the profile so a fast-reconnect
  //    from the Computer account cannot race in and re-create itself.
  await setDoc(doc(db, "chatBans", targetUid), {
    uid: targetUid,
    loginName: typeof targetProfile?.loginName === "string" ? targetProfile.loginName : "",
    email: typeof targetProfile?.email === "string" ? targetProfile.email : "",
    bannedBy: myUid,
    bannedAt: serverTimestamp(),
    reason: "computer-permanent-removal",
  });

  // 3. Delete the chat profile doc.
  let profileDeleted = false;
  if (targetProfile) {
    try {
      await deleteDoc(targetRef);
      profileDeleted = true;
    } catch (e) {
      console.error("[chat] removeComputerFromWebsite: delete profile failed", e);
      throw e;
    }
  }

  return {
    groupsUpdated,
    profileDeleted,
    banned: true,
  };
}

export async function setModeratorRole({ targetUid, enabled, myUid }) {
  return setMemberModerationRole({
    targetUid,
    moderationRole: enabled ? "moderator" : "member",
    myUid,
  });
}

// ── Admin: permanent deletion via privileged Cloud Function ────────────────
// Deleting another user's Firebase Auth login (and recursively wiping a group's
// messages) needs Admin SDK privileges, which only run server-side. These call
// the `chatAdminApi` Cloud Function with the admin's Firebase ID token.
function chatAdminApiUrl(path) {
  if (typeof window !== "undefined") {
    const isLocalHost = ["localhost", "127.0.0.1"].includes(window.location.hostname);
    // Matches the QuizPopz dev fallback: the Firebase hosting emulator on :8787
    // serves the rewrites to the functions emulator.
    if (isLocalHost) return `http://127.0.0.1:8787/api/chat-admin/${path}`;
  }
  return `/api/chat-admin/${path}`;
}

async function callChatAdminApi(path, payload) {
  const user = auth.currentUser;
  if (!user) throw new Error("Sign in as Admin Vivaan first.");
  const idToken = await user.getIdToken();
  let res;
  try {
    res = await fetch(chatAdminApiUrl(path), {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
      body: JSON.stringify(payload || {}),
    });
  } catch (_) {
    throw new Error("Couldn't reach the server. Check your connection and try again.");
  }
  let data = null;
  try { data = await res.json(); } catch (_) { data = null; }
  if (!res.ok || !data || data.ok === false) {
    throw new Error((data && data.error) || "Admin action failed.");
  }
  return data;
}

// Permanently delete a user's account: their Firebase Auth login, chat profile,
// DMs, and group memberships. Irreversible.
export async function deleteChatAccountPermanently({ targetUid }) {
  if (!targetUid) throw new Error("Missing user to delete.");
  return callChatAdminApi("delete-account", { targetUid });
}

// Permanently delete an entire group conversation, including all its messages.
export async function deleteChatGroupPermanently({ convId }) {
  if (!convId) throw new Error("Missing group to delete.");
  return callChatAdminApi("delete-group", { convId });
}

// ── Conversations ──────────────────────────────────────────────────────
// DMs use a deterministic doc id so two people can't accidentally end up with two DMs.
function dmDocId(uidA, uidB) {
  return "dm__" + [uidA, uidB].sort().join("__");
}

function isValidDmForPair(conversation, myUid, otherUid) {
  return !!conversation
    && conversation.type === "dm"
    && Array.isArray(conversation.memberIds)
    && conversation.memberIds.length === 2
    && conversation.memberIds.includes(myUid)
    && conversation.memberIds.includes(otherUid);
}

async function findReadableDm(myUid, otherUid) {
  const snap = await getDocs(
    query(collection(db, "conversations"), where("memberIds", "array-contains", myUid))
  );
  let found = null;
  snap.forEach((d) => {
    if (found) return;
    const conv = sanitizeConversation(d.id, d.data());
    if (
      conv.type === "dm" &&
      conv.memberIds.length === 2 &&
      conv.memberIds.includes(myUid) &&
      conv.memberIds.includes(otherUid)
    ) {
      found = conv;
    }
  });
  return found;
}

async function createFallbackDm(myUid, otherUid) {
  const data = {
    type: "dm",
    name: null,
    memberIds: [myUid, otherUid].sort(),
    createdBy: myUid,
    createdAt: serverTimestamp(),
    lastMessage: null,
  };
  const ref = await addDoc(collection(db, "conversations"), data);
  return sanitizeConversation(ref.id, data);
}

export async function getOrCreateDm(myUid, otherUid) {
  if (myUid === otherUid) throw new Error("Can't DM yourself.");
  const id = dmDocId(myUid, otherUid);
  const ref = doc(db, "conversations", id);
  let exists = false;
  let existing = null;
  try {
    const s = await getDoc(ref);
    exists = !!s.exists();
    existing = s.exists() ? s.data() : null;
  } catch {
    exists = false;
    existing = null;
  }
  if (exists && existing) {
    const deterministicDm = sanitizeConversation(id, existing);
    if (isValidDmForPair(deterministicDm, myUid, otherUid)) {
      return deterministicDm;
    }

    try {
      const readable = await findReadableDm(myUid, otherUid);
      if (readable) return readable;
      return await createFallbackDm(myUid, otherUid);
    } catch (fallbackErr) {
      if (isQuotaExceededError(fallbackErr)) {
        throw new Error(
          "Firestore quota is exhausted right now. New chats are temporarily blocked until quota resets or billing is upgraded."
        );
      }
      if (fallbackErr && fallbackErr.code === "permission-denied") {
        throw new Error(
          "Firestore blocked creating that chat. Reload the page (⌘⇧R) — if still broken, ping an admin."
        );
      }
      throw fallbackErr;
    }
  }
  const data = {
    type: "dm",
    name: null,
    memberIds: [myUid, otherUid].sort(),
    createdBy: myUid,
    createdAt: serverTimestamp(),
    lastMessage: null,
  };
  try {
    await setDoc(ref, data);
  } catch (err) {
    if (isQuotaExceededError(err)) {
      throw new Error(
        "Firestore quota is exhausted right now. New chats are temporarily blocked until quota resets or billing is upgraded."
      );
    }
    if (err && err.code === "permission-denied") {
      try {
        const existing = await findReadableDm(myUid, otherUid);
        if (existing) return existing;
        return await createFallbackDm(myUid, otherUid);
      } catch (fallbackErr) {
        if (isQuotaExceededError(fallbackErr)) {
          throw new Error(
            "Firestore quota is exhausted right now. New chats are temporarily blocked until quota resets or billing is upgraded."
          );
        }
        if (fallbackErr && fallbackErr.code === "permission-denied") {
          throw new Error(
            "Firestore blocked creating that chat. Reload the page (⌘⇧R) — if still broken, ping an admin."
          );
        }
        throw fallbackErr;
      }
    }
    throw err;
  }
  return sanitizeConversation(id, data);
}

export async function createGroup({ myUid, name, memberIds }) {
  const ids = Array.from(new Set([myUid, ...memberIds]));
  if (ids.length < 3) throw new Error("Group needs at least 3 members (including you).");
  if (ids.length > MAX_GROUP_MEMBERS) {
    throw new Error(`Groups are capped at ${MAX_GROUP_MEMBERS} members.`);
  }
  const data = {
    type: "group",
    name: (name || "").trim().slice(0, 60) || "Untitled group",
    memberIds: ids,
    createdBy: myUid,
    createdAt: serverTimestamp(),
    lastMessage: null,
  };
  let ref;
  try {
    ref = await addDoc(collection(db, "conversations"), data);
  } catch (err) {
    if (isQuotaExceededError(err)) {
      throw new Error(
        "Firestore quota is exhausted right now. New chats are temporarily blocked until quota resets or billing is upgraded."
      );
    }
    throw err;
  }
  return sanitizeConversation(ref.id, data);
}

export async function addPeopleToGroup({ convId, myUid, memberIds, name }) {
  const idsToAdd = Array.isArray(memberIds)
    ? memberIds.filter((id) => typeof id === "string" && id.length > 0)
    : [];
  if (!convId) throw new Error("Conversation missing.");
  if (!myUid) throw new Error("You need to be signed in.");
  if (idsToAdd.length === 0) throw new Error("Pick at least one person to add.");

  const convRef = doc(db, "conversations", convId);
  let snap;
  try {
    try {
      snap = await getDocFromServer(convRef);
    } catch {
      snap = await getDoc(convRef);
    }
  } catch (err) {
    console.error("[chat] addPeopleToGroup: get conversation failed", err);
    throw err;
  }

  if (!snap.exists()) throw new Error("Conversation missing — refresh the page.");
  const current = sanitizeConversation(snap.id, snap.data());
  if (current.type !== "group") {
    throw new Error("Only group chats can add people in place.");
  }
  if (!current.memberIds.includes(myUid)) {
    throw new Error("You are no longer in that group.");
  }

  const nextMemberIds = Array.from(new Set([...(current.memberIds || []), ...idsToAdd]));
  if (nextMemberIds.length === current.memberIds.length) return current;
  if (nextMemberIds.length > MAX_GROUP_MEMBERS) {
    throw new Error(`Groups are capped at ${MAX_GROUP_MEMBERS} members.`);
  }

  const nextName = (name || "").trim().slice(0, 60) || current.name || "Untitled group";

  try {
    await updateDoc(convRef, {
      memberIds: nextMemberIds,
      name: nextName,
    });
  } catch (err) {
    if (isQuotaExceededError(err)) {
      throw new Error(
        "Firestore quota is exhausted right now. Updating this group is temporarily blocked until quota resets or billing is upgraded."
      );
    }
    throw err;
  }

  return sanitizeConversation(convRef.id, {
    ...snap.data(),
    memberIds: nextMemberIds,
    name: nextName,
  });
}

async function loadGroupConversationForMutation(convId, label) {
  if (!convId) throw new Error("Conversation missing.");
  const convRef = doc(db, "conversations", convId);
  let snap;
  try {
    try {
      snap = await getDocFromServer(convRef);
    } catch {
      snap = await getDoc(convRef);
    }
  } catch (err) {
    console.error(`[chat] ${label}: get conversation failed`, err);
    throw err;
  }
  if (!snap.exists()) throw new Error("Conversation missing — refresh the page.");
  const current = sanitizeConversation(snap.id, snap.data());
  if (current.type !== "group") throw new Error("Only group chats support that action.");
  return { convRef, snap, current };
}

async function persistGroupConversation(convRef, snap, current, { memberIds, createdBy, name }, quotaMessage) {
  const nextMemberIds = Array.isArray(memberIds) ? memberIds : current.memberIds;
  const nextOwnerId = createdBy || current.createdBy;
  const nextName = typeof name === "string"
    ? (name.trim().slice(0, 60) || current.name || "Untitled group")
    : (current.name || "Untitled group");

  try {
    await updateDoc(convRef, {
      memberIds: nextMemberIds,
      createdBy: nextOwnerId,
      name: nextName,
    });
  } catch (err) {
    if (isQuotaExceededError(err)) {
      throw new Error(quotaMessage);
    }
    throw err;
  }

  return sanitizeConversation(convRef.id, {
    ...snap.data(),
    memberIds: nextMemberIds,
    createdBy: nextOwnerId,
    name: nextName,
  });
}

export async function removeGroupMember({ convId, myUid, targetUid }) {
  if (!myUid) throw new Error("You need to be signed in.");
  if (!targetUid) throw new Error("Pick someone to remove.");
  if (targetUid === myUid) throw new Error("Use leave group to remove yourself.");

  const { convRef, snap, current } = await loadGroupConversationForMutation(convId, "removeGroupMember");
  if (!current.memberIds.includes(targetUid)) return current;

  const nextMemberIds = current.memberIds.filter((uid) => uid !== targetUid);
  if (nextMemberIds.length === 0) {
    throw new Error("Group must keep at least one member.");
  }

  return persistGroupConversation(
    convRef,
    snap,
    current,
    {
      memberIds: nextMemberIds,
      createdBy: current.createdBy === targetUid ? nextMemberIds[0] : current.createdBy,
    },
    "Firestore quota is exhausted right now. Removing that member is temporarily blocked until quota resets or billing is upgraded."
  );
}

export async function leaveGroup({ convId, myUid }) {
  if (!myUid) throw new Error("You need to be signed in.");

  const { convRef, snap, current } = await loadGroupConversationForMutation(convId, "leaveGroup");
  if (!current.memberIds.includes(myUid)) return current;

  const nextMemberIds = current.memberIds.filter((uid) => uid !== myUid);
  if (nextMemberIds.length === 0) {
    throw new Error("Can't leave the last remaining member in a group.");
  }

  return persistGroupConversation(
    convRef,
    snap,
    current,
    {
      memberIds: nextMemberIds,
      createdBy: current.createdBy === myUid ? nextMemberIds[0] : current.createdBy,
    },
    "Firestore quota is exhausted right now. Leaving this group is temporarily blocked until quota resets or billing is upgraded."
  );
}

export async function transferGroupOwnership({ convId, nextOwnerUid }) {
  if (!nextOwnerUid) throw new Error("Pick someone to make owner.");

  const { convRef, snap, current } = await loadGroupConversationForMutation(convId, "transferGroupOwnership");
  if (!current.memberIds.includes(nextOwnerUid)) {
    throw new Error("The new owner must still be in the group.");
  }
  if (current.createdBy === nextOwnerUid) return current;

  return persistGroupConversation(
    convRef,
    snap,
    current,
    { createdBy: nextOwnerUid },
    "Firestore quota is exhausted right now. Transferring ownership is temporarily blocked until quota resets or billing is upgraded."
  );
}

// Live-subscribe to "all conversations I'm a member of", ordered by recency.
// Firestore can't combine `array-contains` with `orderBy` on a different field without a composite index,
// so we sort client-side. With 100 users / a few hundred convs at most, that's free.
export function subscribeMyConversations(myUid, cb, onError) {
  if (!myUid || typeof myUid !== "string") {
    console.warn("[chat] subscribeMyConversations skipped: invalid uid");
    return () => {};
  }
  const q = query(collection(db, "conversations"), where("memberIds", "array-contains", myUid));
  return onSnapshot(
    q,
    (snap) => {
      const arr = [];
      snap.forEach((d) => {
        const conversation = sanitizeConversation(d.id, d.data());
        if (!conversation.memberIds.includes(myUid)) {
          return;
        }
        arr.push(conversation);
      });
      arr.sort((a, b) => recencyTs(b) - recencyTs(a));
      cb(arr);
    },
    (err) => {
      try { err.where = "conversations-list"; err.uidUsed = myUid; } catch (_) {}
      console.error("[chat] conversations listener error:", err);
      onError && onError(err);
    }
  );
}

export function subscribeAnnouncementChannels(cb, onError) {
  const q = query(collection(db, "conversations"), where("channelType", "==", ANNOUNCEMENT_CHANNEL_TYPE));
  return onSnapshot(
    q,
    (snap) => {
      const arr = [];
      snap.forEach((d) => {
        const conversation = sanitizeConversation(d.id, d.data());
        if (!isAnnouncementChannelConversation(conversation)) return;
        if (!conversation.serverId) return;
        arr.push(conversation);
      });
      arr.sort((a, b) => {
        const serverNameCompare = String(a.serverName || "").localeCompare(String(b.serverName || ""));
        if (serverNameCompare !== 0) return serverNameCompare;
        const channelOrderCompare = Number(a.channelOrder || 0) - Number(b.channelOrder || 0);
        if (channelOrderCompare !== 0) return channelOrderCompare;
        return String(a.name || "").localeCompare(String(b.name || ""));
      });
      cb(arr);
    },
    (err) => {
      try { err.where = "announcement-channels"; } catch (_) {}
      console.error("[chat] announcement channels listener error:", err);
      onError && onError(err);
    }
  );
}

export async function ensureAnnouncementsChannel({ myUid, profile }) {
  if (!myUid || !canPostAnnouncementProfile(profile || {})) return null;
  const convRef = doc(db, "conversations", ANNOUNCEMENTS_CHANNEL_ID);
  const snap = await getDoc(convRef);
  if (snap.exists()) {
    return sanitizeConversation(snap.id, snap.data());
  }

  const data = {
    type: "group",
    name: ANNOUNCEMENTS_CHANNEL_NAME,
    memberIds: [myUid],
    createdBy: myUid,
    createdAt: serverTimestamp(),
    lastMessage: null,
    serverId: ANNOUNCEMENTS_SERVER_ID,
    serverName: ANNOUNCEMENTS_SERVER_NAME,
    channelType: ANNOUNCEMENT_CHANNEL_TYPE,
    channelOrder: 0,
  };
  await setDoc(convRef, data);
  return sanitizeConversation(convRef.id, data);
}

function recencyTs(c) {
  if (c.lastMessage && c.lastMessage.createdAt) return tsMs(c.lastMessage.createdAt);
  return tsMs(c.createdAt);
}

// Firestore Timestamp → millis. Tolerates serverTimestamp pending state and plain numbers.
export function tsMs(t) {
  if (!t) return 0;
  if (typeof t === "number") return t;
  if (typeof t.toMillis === "function") return t.toMillis();
  if (typeof t.seconds === "number") return t.seconds * 1000;
  return 0;
}

// ── Messages ───────────────────────────────────────────────────────────
const MESSAGE_PAGE = 200;
const DECRYPT_RETRY_THROTTLE_MS = 30_000;
const DECRYPT_HISTORY_LOOKBACK = 400;

function profilePublicKeyString(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value.zkPublicKey === "string") return value.zkPublicKey;
  return "";
}

function looksLikePublicKey(value) {
  return typeof value === "string" && value.length >= 80;
}

async function resolvePublicKeysForUids(
  uids = [],
  seedProfilesByUid = {},
  { forceRefresh = false } = {}
) {
  const out = {};
  const toFetch = [];
  const seen = new Set();

  uids.forEach((uid) => {
    if (!uid || seen.has(uid)) return;
    seen.add(uid);
    const fromSeed = profilePublicKeyString(seedProfilesByUid[uid]);
    if (fromSeed) {
      out[uid] = fromSeed;
      PUBLIC_KEY_CACHE_BY_UID.set(uid, fromSeed);
    }
    const fromCache = PUBLIC_KEY_CACHE_BY_UID.get(uid);
    if (!fromSeed && fromCache) {
      out[uid] = fromCache;
    }
    if (forceRefresh || !out[uid]) {
      toFetch.push(uid);
    }
  });

  await Promise.all(
    toFetch.map(async (uid) => {
      try {
        const snap = await getDoc(doc(db, "chatProfiles", uid));
        if (!snap.exists()) return;
        const profile = sanitizeChatProfile(uid, snap.data());
        if (profile.zkPublicKey) {
          out[uid] = profile.zkPublicKey;
          PUBLIC_KEY_CACHE_BY_UID.set(uid, profile.zkPublicKey);
        }
      } catch {
        // Best effort only; missing keys are surfaced by decrypt errors.
      }
    })
  );

  return out;
}

async function resolveDmPublicKeyFromRecentMessages({ convId, myUid, recipientUid }) {
  if (!convId || !myUid || !recipientUid) return "";
  try {
    const snap = await getDocs(
      query(
        collection(db, "conversations", convId, "messages"),
        orderBy("createdAt", "desc"),
        fbLimit(80)
      )
    );
    let found = "";
    snap.forEach((d) => {
      if (found) return;
      const message = sanitizeMessage(d.id, d.data());
      const enc = message.enc;
      if (!enc || !enc.iv) return;
      const senderUid = enc.senderUid || message.senderId || "";
      const senderPub = typeof enc.senderPub === "string" ? enc.senderPub : "";
      const recipientPub = typeof enc.recipientPub === "string" ? enc.recipientPub : "";

      if (senderUid === recipientUid && looksLikePublicKey(senderPub)) {
        found = senderPub;
        return;
      }
      if (senderUid === myUid && looksLikePublicKey(recipientPub)) {
        found = recipientPub;
      }
    });
    return found;
  } catch {
    return "";
  }
}

async function resolveConversationPublicKeysFromRecentMessages({ convId, convMemberIds = [] }) {
  if (!convId) return {};
  try {
    const snap = await getDocs(
      query(
        collection(db, "conversations", convId, "messages"),
        orderBy("createdAt", "desc"),
        fbLimit(DECRYPT_HISTORY_LOOKBACK)
      )
    );
    const out = {};
    snap.forEach((d) => {
      const message = sanitizeMessage(d.id, d.data());
      const payload = encryptedPayloadFromMessage(message);
      if (!payload) return;

      const senderUid = payload.senderUid || message.senderId || "";
      if (senderUid && looksLikePublicKey(payload.senderPub) && !looksLikePublicKey(out[senderUid])) {
        out[senderUid] = payload.senderPub;
      }

      const roomMembers = Array.isArray(message.roomMembers) && message.roomMembers.length > 0
        ? message.roomMembers
        : convMemberIds;
      if (roomMembers.length !== 2) return;

      const recipientUid = roomMembers.find((uid) => uid && uid !== senderUid) || "";
      if (recipientUid && looksLikePublicKey(payload.recipientPub) && !looksLikePublicKey(out[recipientUid])) {
        out[recipientUid] = payload.recipientPub;
      }
    });
    return out;
  } catch {
    return {};
  }
}

export async function warmConversationSendCache({
  convId,
  myUid,
  memberIds,
  publicKeysByUid = {},
}) {
  const roomMembers = Array.from(new Set((memberIds || []).filter(Boolean)));
  if (
    !convId
    || !myUid
    || !ZK_ENCRYPTION_ENABLED
    || roomMembers.length < LARGE_GROUP_SEND_PREWARM_THRESHOLD
    || !roomMembers.includes(myUid)
  ) {
    return { warmed: false, memberCount: roomMembers.length, keyMap: {} };
  }

  const warmKey = `${convId}|${myUid}|${roomMembers.slice().sort().join(":")}`;
  const inFlight = SEND_CACHE_WARM_PROMISES.get(warmKey);
  if (inFlight) return inFlight;

  const warmPromise = (async () => {
    const keyMap = await resolvePublicKeysForUids(roomMembers, publicKeysByUid, { forceRefresh: false });
    const missingMembers = roomMembers.filter((uid) => !looksLikePublicKey(keyMap[uid]));
    if (missingMembers.length > 0) {
      const recentConversationKeys = await resolveConversationPublicKeysFromRecentMessages({
        convId,
        convMemberIds: roomMembers,
      });
      missingMembers.forEach((uid) => {
        const recentKey = recentConversationKeys[uid];
        if (!looksLikePublicKey(recentKey)) return;
        keyMap[uid] = recentKey;
        PUBLIC_KEY_CACHE_BY_UID.set(uid, recentKey);
      });
    }

    await warmGroupMessageEncryption({
      chatId: convId,
      senderUid: myUid,
      participantUids: roomMembers,
      publicKeysByUid: keyMap,
    });

    return {
      warmed: true,
      memberCount: roomMembers.length,
      keyMap,
    };
  })();

  SEND_CACHE_WARM_PROMISES.set(warmKey, warmPromise);
  warmPromise.finally(() => {
    if (SEND_CACHE_WARM_PROMISES.get(warmKey) === warmPromise) {
      SEND_CACHE_WARM_PROMISES.delete(warmKey);
    }
  });
  return warmPromise;
}

function encryptedPayloadFromMessage(message) {
  if (!message?.enc || !message?.body) return null;
  if (!message.enc.iv) return null;
  return {
    v: message.enc.v || 1,
    alg: message.enc.alg || "AES-GCM",
    kind: message.enc.kind || "",
    senderUid: message.enc.senderUid || message.senderId,
    senderPub: message.enc.senderPub || "",
    recipientPub: message.enc.recipientPub || "",
    iv: message.enc.iv,
    ciphertext: message.body,
    senderKeyVersion: message.enc.senderKeyVersion || 1,
    senderKeyBoxes: message.enc.senderKeyBoxes || {},
  };
}

function senderKeyPacketCacheKey(senderUid, version) {
  return `${String(senderUid || "")}::${Number(version || 1)}`;
}

function senderKeyPacketCandidates(senderKeyPacketCache = {}, senderUid, preferredVersion) {
  if (!senderUid) return [];
  const preferred = Number(preferredVersion || 1);
  const exactKey = senderKeyPacketCacheKey(senderUid, preferred);
  const out = [];

  if (senderKeyPacketCache[exactKey]) {
    out.push({ version: preferred, packet: senderKeyPacketCache[exactKey] });
  }

  Object.entries(senderKeyPacketCache || {}).forEach(([key, packet]) => {
    const parts = String(key || "").split("::");
    if (parts.length !== 2) return;
    if (parts[0] !== senderUid) return;
    const version = Number(parts[1] || 1);
    if (version === preferred) return;
    out.push({ version, packet });
  });

  return out;
}

function buildSenderKeyPacketCache(messages = [], myUid) {
  const cache = {};
  if (!myUid) return cache;

  for (let i = 0; i < messages.length; i += 1) {
    const message = messages[i];
    const payload = encryptedPayloadFromMessage(message);
    if (!payload) continue;
    const hasGroupPackets = !!(payload.senderKeyBoxes && Object.keys(payload.senderKeyBoxes).length > 0);
    if (!(payload.kind === "group" || hasGroupPackets)) continue;

    const senderUid = payload.senderUid || message.senderId || "";
    const senderKeyVersion = Number(payload.senderKeyVersion || 1);
    const packet = payload.senderKeyBoxes?.[myUid];
    if (!senderUid || !packet) continue;

    const key = senderKeyPacketCacheKey(senderUid, senderKeyVersion);
    if (!cache[key]) {
      cache[key] = packet;
    }
  }

  return cache;
}

// A group message wraps the sender key for every member present at send time
// (see `senderKeyBoxes` in encryptMessage). A message that carries sender-key
// packets but has none for `myUid` — and for which no other message from the
// same sender/version supplied a packet for `myUid` either — was sent before
// this user joined the group, so it should be hidden from them. Checking the
// shared packet cache avoids hiding messages a member could otherwise decrypt
// (e.g. their public key was momentarily unresolved on a single send).
function isPreJoinGroupMessage(message, myUid, senderKeyPacketCache = {}) {
  if (!myUid) return false;
  const boxes = message?.enc?.senderKeyBoxes;
  if (!boxes || typeof boxes !== "object") return false;
  if (Object.keys(boxes).length === 0) return false;
  if (message.senderId === myUid) return false;
  if (boxes[myUid]) return false;

  const senderUid = message.enc?.senderUid || message.senderId || "";
  const version = Number(message.enc?.senderKeyVersion || 1);
  if (senderKeyPacketCache[senderKeyPacketCacheKey(senderUid, version)]) return false;

  return true;
}

function uniquePublicKeys(values = []) {
  return Array.from(new Set(values.filter((value) => looksLikePublicKey(value))));
}

function buildDmPublicKeyCache(messages = [], convMemberIds = []) {
  const cache = {};

  function push(uid, key) {
    if (!uid || !looksLikePublicKey(key)) return;
    if (!cache[uid]) cache[uid] = [];
    if (!cache[uid].includes(key)) {
      cache[uid].push(key);
    }
  }

  for (let i = 0; i < messages.length; i += 1) {
    const message = messages[i];
    const payload = encryptedPayloadFromMessage(message);
    if (!payload) continue;

    const senderUid = payload.senderUid || message.senderId || "";
    const roomMembers = Array.isArray(message.roomMembers) && message.roomMembers.length > 0
      ? message.roomMembers
      : convMemberIds;
    const recipientUid = roomMembers.find((uid) => uid && uid !== senderUid) || "";

    push(senderUid, payload.senderPub);
    push(recipientUid, payload.recipientPub);
  }

  return cache;
}

function mergeDmPublicKeyCaches(baseCache = {}, nextCache = {}) {
  const out = {};

  Object.entries(baseCache || {}).forEach(([uid, keys]) => {
    out[uid] = Array.isArray(keys)
      ? uniquePublicKeys(keys)
      : [];
  });

  Object.entries(nextCache || {}).forEach(([uid, keys]) => {
    const current = out[uid] || [];
    const merged = current.concat(Array.isArray(keys) ? keys : []);
    out[uid] = uniquePublicKeys(merged);
  });

  return out;
}

async function buildHistoricalDecryptCaches({ convId, myUid, convMemberIds = [] }) {
  if (!convId || !myUid) {
    return {
      senderKeyPacketCache: {},
      dmPublicKeyCache: {},
    };
  }

  try {
    const snap = await getDocs(
      query(
        collection(db, "conversations", convId, "messages"),
        orderBy("createdAt", "desc"),
        fbLimit(DECRYPT_HISTORY_LOOKBACK)
      )
    );
    const rows = [];
    snap.forEach((d) => rows.push(sanitizeMessage(d.id, d.data())));
    return {
      senderKeyPacketCache: buildSenderKeyPacketCache(rows, myUid),
      dmPublicKeyCache: buildDmPublicKeyCache(rows, convMemberIds),
    };
  } catch {
    return {
      senderKeyPacketCache: {},
      dmPublicKeyCache: {},
    };
  }
}

async function decryptIncomingMessage({
  message,
  convId,
  myUid,
  convType,
  convMemberIds,
  publicKeysByUid,
  senderKeyPacketCache,
  dmPublicKeyCache,
}) {
  let payload = encryptedPayloadFromMessage(message);
  if (!payload) return message;

  const memberIds = Array.isArray(message.roomMembers) && message.roomMembers.length > 0
    ? message.roomMembers
    : convMemberIds;
  const hasGroupPackets = !!(payload.senderKeyBoxes && Object.keys(payload.senderKeyBoxes).length > 0);
  const hasGroupShape = convType === "group" || memberIds.length > 2 || hasGroupPackets;
  const effectiveKind = hasGroupShape ? "group" : "dm";
  const otherUid = memberIds.find((uid) => uid !== myUid) || message.senderId;
  const senderUid = payload.senderUid || message.senderId || "";
  const senderKeyVersion = Number(payload.senderKeyVersion || 1);

  if (effectiveKind === "dm") {
    const senderSnapshots = uniquePublicKeys([
      payload.senderPub,
      ...((dmPublicKeyCache && senderUid && dmPublicKeyCache[senderUid]) || []),
      senderUid ? publicKeysByUid[senderUid] : "",
    ]);
    const recipientSnapshots = uniquePublicKeys([
      payload.recipientPub,
      ...((dmPublicKeyCache && otherUid && dmPublicKeyCache[otherUid]) || []),
      otherUid ? publicKeysByUid[otherUid] : "",
    ]);
    payload = {
      ...payload,
      senderPub: senderSnapshots[0] || payload.senderPub || "",
      recipientPub: recipientSnapshots[0] || payload.recipientPub || "",
    };
  }

  if (effectiveKind === "group") {
    const hasDirectPacket = !!payload.senderKeyBoxes?.[myUid];
    if (!hasDirectPacket && senderUid) {
      const fallbackPacket = senderKeyPacketCache?.[senderKeyPacketCacheKey(senderUid, senderKeyVersion)];
      if (fallbackPacket) {
        payload = {
          ...payload,
          senderKeyBoxes: {
            ...(payload.senderKeyBoxes || {}),
            [myUid]: fallbackPacket,
          },
        };
      }
    }
  }

  try {
    const decryptedBody = await decryptZkMessage({
      chatId: convId,
      payload,
      myUid,
      kind: effectiveKind,
      senderUid: message.senderId,
      otherUid,
      publicKeysByUid,
    });
    return {
      ...message,
      body: decryptedBody,
    };
  } catch (error) {
    if (effectiveKind === "dm") {
      const senderIsMe = (message.senderId || senderUid) === myUid;
      const peerUid = senderIsMe ? otherUid : senderUid;
      const peerPublicKeyCandidates = uniquePublicKeys([
        senderIsMe ? payload.recipientPub : payload.senderPub,
        ...((dmPublicKeyCache && peerUid && dmPublicKeyCache[peerUid]) || []),
        peerUid ? publicKeysByUid[peerUid] : "",
      ]);

      for (let i = 0; i < peerPublicKeyCandidates.length; i += 1) {
        const peerPublicKey = peerPublicKeyCandidates[i];
        const altPayload = senderIsMe
          ? { ...payload, recipientPub: peerPublicKey }
          : { ...payload, senderPub: peerPublicKey };
        try {
          const decryptedBody = await decryptZkMessage({
            chatId: convId,
            payload: altPayload,
            myUid,
            kind: "dm",
            senderUid: message.senderId,
            otherUid,
            publicKeysByUid,
          });
          return {
            ...message,
            body: decryptedBody,
          };
        } catch {
          // keep trying candidates
        }
      }
    }

    if (effectiveKind === "group" && senderUid) {
      const packetCandidates = senderKeyPacketCandidates(senderKeyPacketCache, senderUid, senderKeyVersion);
      const directPacket = payload.senderKeyBoxes?.[myUid];
      if (directPacket) {
        packetCandidates.unshift({ version: senderKeyVersion, packet: directPacket });
      }

      const senderPublicKeyCandidates = uniquePublicKeys([
        payload.senderPub,
        ...((dmPublicKeyCache && senderUid && dmPublicKeyCache[senderUid]) || []),
        senderUid ? publicKeysByUid[senderUid] : "",
      ]);
      const senderPublicKeysToTry = senderPublicKeyCandidates.length > 0
        ? senderPublicKeyCandidates
        : [payload.senderPub || ""];

      const seenPacketCandidates = new Set();
      for (let i = 0; i < packetCandidates.length; i += 1) {
        const candidate = packetCandidates[i];
        const packet = candidate?.packet;
        const candidateVersion = Number(candidate?.version || senderKeyVersion);
        if (!packet) continue;

        const packetKey = `${candidateVersion}:${packet.iv || ""}:${packet.ciphertext || ""}`;
        if (seenPacketCandidates.has(packetKey)) continue;
        seenPacketCandidates.add(packetKey);

        for (let j = 0; j < senderPublicKeysToTry.length; j += 1) {
          const senderPub = senderPublicKeysToTry[j] || payload.senderPub || "";
          const altPayload = {
            ...payload,
            senderPub,
            senderKeyVersion: candidateVersion,
            senderKeyBoxes: {
              ...(payload.senderKeyBoxes || {}),
              [myUid]: packet,
            },
          };
          try {
            const decryptedBody = await decryptZkMessage({
              chatId: convId,
              payload: altPayload,
              myUid,
              kind: "group",
              senderUid: message.senderId,
              otherUid,
              publicKeysByUid,
            });
            if (looksLikePublicKey(senderPub) && !looksLikePublicKey(publicKeysByUid[senderUid])) {
              publicKeysByUid[senderUid] = senderPub;
            }
            return {
              ...message,
              body: decryptedBody,
            };
          } catch {
            // keep trying candidates
          }
        }
      }
    }
    return {
      ...message,
      body: "[Unable to decrypt message]",
      decryptError: error?.message || "decrypt-failed",
    };
  }
}

// Live-subscribe to the most recent N messages (newest LAST in the callback array).
// Message access follows current conversation membership so adding people to a group
// keeps everyone in the same thread instead of forcing a brand-new conversation.
export function subscribeMessages(convId, myUid, cb, onError, seedProfilesByUid = {}) {
  if (!myUid || typeof myUid !== "string") {
    console.warn("[chat] subscribeMessages skipped: invalid uid");
    return () => {};
  }

  let convTypeCache = "dm";
  let convMemberIdsCache = [];
  let lastKeyRefreshAttemptMs = 0;
  let lastCloudRecoverAttemptMs = 0;
  let lastHistoryReloadAttemptMs = 0;
  const ensureConversationInfo = async () => {
    if (convMemberIdsCache.length > 0) return;
    const snap = await getDoc(doc(db, "conversations", convId));
    if (!snap.exists()) return;
    const conv = sanitizeConversation(snap.id, snap.data());
    convTypeCache = conv.type;
    convMemberIdsCache = conv.memberIds || [];
  };

  const q = query(
    collection(db, "conversations", convId, "messages"),
    orderBy("createdAt", "desc"),
    fbLimit(MESSAGE_PAGE)
  );
  return onSnapshot(
    q,
    (snap) => {
      Promise.resolve()
        .then(async () => {
          await ensureConversationInfo().catch(() => {});
          let arr = [];
          snap.forEach((d) => arr.push(sanitizeMessage(d.id, d.data())));
          arr.reverse();

          let senderKeyPacketCache = buildSenderKeyPacketCache(arr, myUid);
          let dmPublicKeyCache = buildDmPublicKeyCache(arr, convMemberIdsCache);

          // Hide group history sent before the current user joined the group.
          if (convTypeCache === "group") {
            arr = arr.filter(
              (message) => !isPreJoinGroupMessage(message, myUid, senderKeyPacketCache)
            );
          }

          const decryptUids = new Set(convTypeCache === "dm" ? convMemberIdsCache : []);
          arr.forEach((message) => {
            if (message.senderId) decryptUids.add(message.senderId);
            if (convTypeCache === "dm") {
              (message.roomMembers || []).forEach((uid) => decryptUids.add(uid));
            }
            if (message.enc?.senderUid) decryptUids.add(message.enc.senderUid);
          });

          const publicKeysByUid = await resolvePublicKeysForUids(Array.from(decryptUids), seedProfilesByUid);
          const out = [];
          const retryIndexes = [];

          for (let i = 0; i < arr.length; i += 1) {
            const decrypted = await decryptIncomingMessage({
              message: arr[i],
              convId,
              myUid,
              convType: convTypeCache,
              convMemberIds: convMemberIdsCache,
              publicKeysByUid,
              senderKeyPacketCache,
              dmPublicKeyCache,
            });
            out.push(decrypted);
            if (
              decrypted.body === "[Unable to decrypt message]"
              && encryptedPayloadFromMessage(arr[i])
            ) {
              retryIndexes.push(i);
            }
          }

          // Second pass: another message from the same sender/version may have supplied
          // the missing sender-key packet during the first pass.
          for (let i = 0; i < retryIndexes.length; i += 1) {
            const idx = retryIndexes[i];
            const retried = await decryptIncomingMessage({
              message: arr[idx],
              convId,
              myUid,
              convType: convTypeCache,
              convMemberIds: convMemberIdsCache,
              publicKeysByUid,
              senderKeyPacketCache,
              dmPublicKeyCache,
            });
            if (retried.body !== "[Unable to decrypt message]") {
              out[idx] = retried;
            }
          }

          const unresolvedIndexes = [];
          for (let i = 0; i < out.length; i += 1) {
            if (
              out[i].body === "[Unable to decrypt message]"
              && encryptedPayloadFromMessage(arr[i])
            ) {
              unresolvedIndexes.push(i);
            }
          }

          if (unresolvedIndexes.length > 0 && Date.now() - lastKeyRefreshAttemptMs >= DECRYPT_RETRY_THROTTLE_MS) {
            lastKeyRefreshAttemptMs = Date.now();
            const refreshedKeys = await resolvePublicKeysForUids(
              Array.from(decryptUids),
              seedProfilesByUid,
              { forceRefresh: true }
            );
            Object.assign(publicKeysByUid, refreshedKeys);

            for (let i = 0; i < unresolvedIndexes.length; i += 1) {
              const idx = unresolvedIndexes[i];
              const retriedWithFreshKeys = await decryptIncomingMessage({
                message: arr[idx],
                convId,
                myUid,
                convType: convTypeCache,
                convMemberIds: convMemberIdsCache,
                publicKeysByUid,
                senderKeyPacketCache,
                dmPublicKeyCache,
              });
              if (retriedWithFreshKeys.body !== "[Unable to decrypt message]") {
                out[idx] = retriedWithFreshKeys;
              }
            }

            unresolvedIndexes.length = 0;
            for (let i = 0; i < out.length; i += 1) {
              if (
                out[i].body === "[Unable to decrypt message]"
                && encryptedPayloadFromMessage(arr[i])
              ) {
                unresolvedIndexes.push(i);
              }
            }
          }

          if (unresolvedIndexes.length > 0 && Date.now() - lastCloudRecoverAttemptMs >= DECRYPT_RETRY_THROTTLE_MS) {
            lastCloudRecoverAttemptMs = Date.now();
            const recoverResult = await syncChatKeysAcrossDevices({
              uid: myUid,
              recover: true,
              upload: false,
            }).catch(() => ({ recovered: false }));

            if (recoverResult && recoverResult.recovered) {
              for (let i = 0; i < unresolvedIndexes.length; i += 1) {
                const idx = unresolvedIndexes[i];
                const retriedAfterCloudRecover = await decryptIncomingMessage({
                  message: arr[idx],
                  convId,
                  myUid,
                  convType: convTypeCache,
                  convMemberIds: convMemberIdsCache,
                  publicKeysByUid,
                  senderKeyPacketCache,
                  dmPublicKeyCache,
                });
                if (retriedAfterCloudRecover.body !== "[Unable to decrypt message]") {
                  out[idx] = retriedAfterCloudRecover;
                }
              }
            }

            unresolvedIndexes.length = 0;
            for (let i = 0; i < out.length; i += 1) {
              if (
                out[i].body === "[Unable to decrypt message]"
                && encryptedPayloadFromMessage(arr[i])
              ) {
                unresolvedIndexes.push(i);
              }
            }
          }

          if (unresolvedIndexes.length > 0 && Date.now() - lastHistoryReloadAttemptMs >= DECRYPT_RETRY_THROTTLE_MS) {
            lastHistoryReloadAttemptMs = Date.now();
            const historicalCaches = await buildHistoricalDecryptCaches({
              convId,
              myUid,
              convMemberIds: convMemberIdsCache,
            });

            senderKeyPacketCache = {
              ...(historicalCaches.senderKeyPacketCache || {}),
              ...senderKeyPacketCache,
            };
            dmPublicKeyCache = mergeDmPublicKeyCaches(dmPublicKeyCache, historicalCaches.dmPublicKeyCache);

            Object.entries(dmPublicKeyCache).forEach(([uid, keys]) => {
              if (looksLikePublicKey(publicKeysByUid[uid])) return;
              const candidate = Array.isArray(keys) ? keys[0] : "";
              if (looksLikePublicKey(candidate)) {
                publicKeysByUid[uid] = candidate;
              }
            });

            for (let i = 0; i < unresolvedIndexes.length; i += 1) {
              const idx = unresolvedIndexes[i];
              const retriedAfterHistoryReload = await decryptIncomingMessage({
                message: arr[idx],
                convId,
                myUid,
                convType: convTypeCache,
                convMemberIds: convMemberIdsCache,
                publicKeysByUid,
                senderKeyPacketCache,
                dmPublicKeyCache,
              });
              if (retriedAfterHistoryReload.body !== "[Unable to decrypt message]") {
                out[idx] = retriedAfterHistoryReload;
              }
            }
          }

          cb(out);

          // Keep account-synced keys fresh so other browsers/devices on the same account
          // can decrypt without manual recovery steps.
          const hasEncryptedMessages = arr.some((message) => !!encryptedPayloadFromMessage(message));
          if (hasEncryptedMessages) {
            syncChatKeysAcrossDevices({
              uid: myUid,
              recover: false,
              upload: true,
            }).catch(() => {});
          }
        })
        .catch((err) => {
          onError && onError(err);
        });
    },
    (err) => {
      try { err.where = "messages-list"; err.uidUsed = myUid; err.convId = convId; } catch (_) {}
      console.error("[chat] messages listener error:", err);
      onError && onError(err);
    }
  );
}

async function updateConversationPreviewIfNeeded({ convId, message, previousPreview, nextPreview }) {
  if (!convId || !message) return;
  let snap;
  try {
    snap = await getDoc(doc(db, "conversations", convId));
  } catch {
    return;
  }
  if (!snap.exists()) return;
  const conversation = sanitizeConversation(snap.id, snap.data());
  const lastMessage = conversation.lastMessage;
  if (!lastMessage) return;
  if ((lastMessage.senderId || "") !== (message.senderId || "")) return;
  if (tsMs(lastMessage.createdAt) !== tsMs(message.createdAt)) return;
  if ((lastMessage.body || "") !== (previousPreview || "")) return;

  await updateDoc(doc(db, "conversations", convId), {
    lastMessage: {
      body: String(nextPreview || "").slice(0, 200),
      senderId: message.senderId,
      createdAt: message.createdAt,
    },
  }).catch(() => {});
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Couldn't read that image."));
    reader.readAsDataURL(file);
  });
}

async function imageFileToDataUrl(imageFile) {
  if (!imageFile) return "";
  const mimeType = String(imageFile.type || "").toLowerCase();
  if (!mimeType.startsWith("image/")) {
    throw new Error("Only image files are allowed.");
  }
  if (imageFile.size <= 0) {
    throw new Error("That image file is empty.");
  }
  if (imageFile.size > MAX_IMAGE_FILE_BYTES) {
    throw new Error("Images must be 350 KB or smaller.");
  }
  const dataUrl = await readFileAsDataUrl(imageFile);
  if (!isValidImageDataUrl(dataUrl)) {
    throw new Error("That image format is not supported.");
  }
  return dataUrl;
}

// Send a message. We:
//   1) Re-read canonical memberIds from Firestore — UI state can be stale; rules compare to server.
//   2) write the message in the subcollection
//   3) update the conversation's lastMessage preview so sidebars refresh
// Both writes are independent; a small race is harmless (at worst the preview lags one render).
export async function sendMessage({
  convId,
  myUid,
  body,
  memberIds,
  replyTo,
  forwardedFrom,
  imageFile,
  publicKeysByUid = {},
}) {
  const trimmed = String(body || "").trim();
  if (trimmed.length > MAX_BODY_LENGTH) throw new Error("message too long");
  if (!Array.isArray(memberIds) || memberIds.length < 1 || !memberIds.includes(myUid)) {
    throw new Error("invalid conversation membership — reopen the chat or start a new DM.");
  }
  const imageDataUrl = await imageFileToDataUrl(imageFile);
  if (!trimmed && !imageDataUrl) throw new Error("empty message");

  const convRef = doc(db, "conversations", convId);
  // Fast path: use the cached conversation snapshot (the active listener keeps it
  // warm). Avoid forcing a server round-trip on every send — that adds latency
  // proportional to RTT and pressures the rules engine on large groups.
  let snap;
  try {
    snap = await getDoc(convRef);
  } catch (e) {
    console.error("[chat] sendMessage: get conversation failed", e);
    throw e;
  }
  if (!snap.exists()) {
    // Cache miss only — fall back to a server fetch.
    try {
      snap = await getDocFromServer(convRef);
    } catch (e) {
      console.error("[chat] sendMessage: get conversation (server) failed", e);
      throw e;
    }
  }
  if (!snap.exists()) throw new Error("Conversation missing — refresh the page.");
  let conversation = sanitizeConversation(snap.id, snap.data());
  const isAnnouncementChannel = isAnnouncementChannelConversation(conversation);
  let serverIds = conversation.memberIds;
  if (!isAnnouncementChannel && (!Array.isArray(serverIds) || serverIds.length < 1 || !serverIds.includes(myUid))) {
    // Cached snapshot may be stale (e.g. we were just added); confirm with server.
    try {
      const fresh = await getDocFromServer(convRef);
      if (fresh.exists()) {
        conversation = sanitizeConversation(fresh.id, fresh.data());
        if (!isAnnouncementChannelConversation(conversation)) {
          serverIds = conversation.memberIds;
        }
      }
    } catch {
      // fall through to membership error below
    }
    if (!Array.isArray(serverIds) || serverIds.length < 1 || !serverIds.includes(myUid)) {
      throw new Error("invalid conversation membership — reopen the chat or start a new DM.");
    }
  }
  let roomMembers = isAnnouncementChannel ? [] : serverIds.slice();
  let keyMap = {};
  let recentConversationKeys = {};
  if (trimmed && ZK_ENCRYPTION_ENABLED && !isAnnouncementChannel) {
    if (conversation.type === "group" && roomMembers.length >= LARGE_GROUP_SEND_PREWARM_THRESHOLD) {
      try {
        const warmed = await warmConversationSendCache({
          convId,
          myUid,
          memberIds: roomMembers,
          publicKeysByUid,
        });
        keyMap = warmed?.keyMap || {};
      } catch {
        keyMap = {};
      }
    }
    // Use seeded profile cache + in-memory cache first; only fetch profiles for
    // members whose keys are not already known. For a 150-person group this
    // drops ~150 per-send chatProfiles reads down to 0 in the steady state.
    if (Object.keys(keyMap).length === 0) {
      keyMap = await resolvePublicKeysForUids(
        roomMembers,
        publicKeysByUid,
        { forceRefresh: false }
      );
      const missingMembers = roomMembers.filter((uid) => !looksLikePublicKey(keyMap[uid]));
      if (missingMembers.length > 0) {
        recentConversationKeys = await resolveConversationPublicKeysFromRecentMessages({
          convId,
          convMemberIds: roomMembers,
        });
        missingMembers.forEach((uid) => {
          const recentKey = recentConversationKeys[uid];
          if (!looksLikePublicKey(recentKey)) return;
          keyMap[uid] = recentKey;
          PUBLIC_KEY_CACHE_BY_UID.set(uid, recentKey);
        });
      }
    }
  }

  let encryptedTextPayload = null;
  let sentAsEncrypted = false;
  if (trimmed && ZK_ENCRYPTION_ENABLED && !isAnnouncementChannel) {
    const otherUid = conversation.type === "dm"
      ? roomMembers.find((uid) => uid !== myUid)
      : "";

    if (conversation.type === "dm" && otherUid) {
      const fromHistory = recentConversationKeys[otherUid] || await resolveDmPublicKeyFromRecentMessages({
        convId,
        myUid,
        recipientUid: otherUid,
      });
      if (looksLikePublicKey(fromHistory)) {
        keyMap[otherUid] = fromHistory;
        PUBLIC_KEY_CACHE_BY_UID.set(otherUid, fromHistory);
      }
    }
    if (conversation.type === "dm" && otherUid && !looksLikePublicKey(keyMap[otherUid])) {
      throw new Error("Recipient encryption key is missing. Ask them to open chat once, then try again.");
    }

    encryptedTextPayload = await encryptZkMessage({
      chatId: convId,
      text: trimmed,
      senderUid: myUid,
      kind: conversation.type,
      recipientUid: otherUid,
      participantUids: roomMembers,
      publicKeysByUid: keyMap,
    });
    sentAsEncrypted = true;
  }
  const encMeta = encryptedTextPayload
    ? Object.fromEntries(
        Object.entries(encryptedTextPayload).filter(([key]) => key !== "ciphertext")
      )
    : null;

  const messagesCol = collection(db, "conversations", convId, "messages");
  let ref;
  const buildPayload = (members) => {
    const payload = {
      senderId: myUid,
      body: encryptedTextPayload ? encryptedTextPayload.ciphertext : trimmed,
      createdAt: serverTimestamp(),
      roomMembers: members,
    };
    if (encMeta) payload.enc = encMeta;
    if (imageDataUrl) payload.imageDataUrl = imageDataUrl;
    if (replyTo && replyTo.id) {
      payload.replyTo = {
        id: String(replyTo.id),
        senderId: String(replyTo.senderId || ""),
        body: String(describeMessagePreview(replyTo) || "Reply").slice(0, 120),
      };
    }
    if (forwardedFrom && forwardedFrom.senderId) {
      payload.forwardedFrom = {
        senderId: String(forwardedFrom.senderId || ""),
        conversationId: String(forwardedFrom.conversationId || ""),
        conversationName: String(forwardedFrom.conversationName || "").slice(0, 80),
      };
    }
    return payload;
  };
  try {
    ref = await addDoc(messagesCol, buildPayload(roomMembers));
  } catch (e) {
    // canCreateMessage requires roomMembers to mirror conversation.memberIds. If
    // membership changed between our cached read and this write, the rule denies
    // the create. Refetch the conversation from the server once and retry with
    // up-to-date members so transient races don't surface as user errors.
    if (!isAnnouncementChannel && e && e.code === "permission-denied") {
      try {
        const fresh = await getDocFromServer(convRef);
        if (fresh.exists()) {
          const freshConv = sanitizeConversation(fresh.id, fresh.data());
          const freshIds = Array.isArray(freshConv.memberIds) ? freshConv.memberIds : [];
          if (freshIds.includes(myUid)) {
            roomMembers = freshIds.slice();
            ref = await addDoc(messagesCol, buildPayload(roomMembers));
          } else {
            throw e;
          }
        } else {
          throw e;
        }
      } catch (retryErr) {
        console.error("[chat] sendMessage: addDoc message failed", retryErr || e);
        throw retryErr || e;
      }
    } else {
      console.error("[chat] sendMessage: addDoc message failed", e);
      throw e;
    }
  }
  // Best-effort preview update; ignore errors (e.g. transient permission flicker).
  updateDoc(convRef, {
    lastMessage: {
      body: trimmed
        ? (sentAsEncrypted ? "Encrypted message" : trimmed.slice(0, 200))
        : describeMessagePreview({ body: "", imageDataUrl }).slice(0, 200),
      senderId: myUid,
      createdAt: serverTimestamp(),
    },
  }).catch(() => {});
  syncChatKeysAcrossDevices({
    uid: myUid,
    recover: false,
    upload: true,
  }).catch(() => {});
  return ref.id;
}

export async function editMessage({ convId, message, myUid, body, publicKeysByUid = {} }) {
  if (!convId || !message || !message.id) return;
  if (message.senderId !== myUid) throw new Error("You can only edit your own messages.");
  if (message.deletedForEveryoneAt) throw new Error("You can't edit a deleted message.");

  const trimmed = String(body || "").trim();
  if (!trimmed) {
    throw new Error("Message can't be empty.");
  }
  if (trimmed.length > MAX_BODY_LENGTH) throw new Error("message too long");

  const convSnap = await getDoc(doc(db, "conversations", convId));
  if (!convSnap.exists()) {
    throw new Error("Conversation missing — refresh the page.");
  }
  const conv = sanitizeConversation(convSnap.id, convSnap.data());
  const isAnnouncementChannel = isAnnouncementChannelConversation(conv);
  const roomMembers = isAnnouncementChannel
    ? []
    : (Array.isArray(conv.memberIds) ? conv.memberIds : []);
  if (!isAnnouncementChannel && !roomMembers.includes(myUid)) {
    throw new Error("invalid conversation membership — reopen the chat or start a new DM.");
  }
  let keyMap = {};
  let recentConversationKeys = {};
  if (ZK_ENCRYPTION_ENABLED && !isAnnouncementChannel) {
    keyMap = await resolvePublicKeysForUids(
      roomMembers,
      publicKeysByUid,
      { forceRefresh: false }
    );
    const missingMembers = roomMembers.filter((uid) => !looksLikePublicKey(keyMap[uid]));
    if (missingMembers.length > 0) {
      recentConversationKeys = await resolveConversationPublicKeysFromRecentMessages({
        convId,
        convMemberIds: roomMembers,
      });
      missingMembers.forEach((uid) => {
        const recentKey = recentConversationKeys[uid];
        if (!looksLikePublicKey(recentKey)) return;
        keyMap[uid] = recentKey;
        PUBLIC_KEY_CACHE_BY_UID.set(uid, recentKey);
      });
    }
  }
  const otherUid = conv.type === "dm"
    ? roomMembers.find((uid) => uid !== myUid)
    : "";
  let encryptedTextPayload = null;
  let sentAsEncrypted = false;
  if (ZK_ENCRYPTION_ENABLED && !isAnnouncementChannel) {
    if (conv.type === "dm" && otherUid) {
      const fromHistory = recentConversationKeys[otherUid] || await resolveDmPublicKeyFromRecentMessages({
        convId,
        myUid,
        recipientUid: otherUid,
      });
      if (looksLikePublicKey(fromHistory)) {
        keyMap[otherUid] = fromHistory;
        PUBLIC_KEY_CACHE_BY_UID.set(otherUid, fromHistory);
      }
    }
    if (conv.type === "dm" && otherUid && !looksLikePublicKey(keyMap[otherUid])) {
      throw new Error("Recipient encryption key is missing. Ask them to open chat once, then try again.");
    }

    encryptedTextPayload = await encryptZkMessage({
      chatId: convId,
      text: trimmed,
      senderUid: myUid,
      kind: conv.type,
      recipientUid: otherUid,
      participantUids: roomMembers,
      publicKeysByUid: keyMap,
    });
    sentAsEncrypted = true;
  }
  const encMeta = encryptedTextPayload
    ? Object.fromEntries(
        Object.entries(encryptedTextPayload).filter(([key]) => key !== "ciphertext")
      )
    : null;

  const previousPreview = message.enc ? "Encrypted message" : describeMessagePreview(message);
  const nextPreview = sentAsEncrypted ? "Encrypted message" : trimmed;

  if (encMeta) {
    await updateDoc(doc(db, "conversations", convId, "messages", message.id), {
      body: encryptedTextPayload.ciphertext,
      enc: encMeta,
      editedAt: serverTimestamp(),
    });
  } else {
    await updateDoc(doc(db, "conversations", convId, "messages", message.id), {
      body: trimmed,
      enc: deleteField(),
      editedAt: serverTimestamp(),
    });
  }

  await updateConversationPreviewIfNeeded({
    convId,
    message,
    previousPreview,
    nextPreview,
  });

  syncChatKeysAcrossDevices({
    uid: myUid,
    recover: false,
    upload: true,
  }).catch(() => {});
}

export async function hideMessageForMe({ convId, messageId, myUid }) {
  if (!convId || !messageId || !myUid) return;
  await updateDoc(doc(db, "conversations", convId, "messages", messageId), {
    hiddenFor: arrayUnion(myUid),
  });
}

export async function deleteMessageForEveryone({ convId, message, myUid }) {
  if (!convId || !message || !message.id) return;
  if (message.senderId !== myUid) throw new Error("You can only delete your own messages for everyone.");
  if (message.deletedForEveryoneAt) return;

  const previousPreview = describeMessagePreview(message);

  await updateDoc(doc(db, "conversations", convId, "messages", message.id), {
    body: "",
    imageDataUrl: null,
    replyTo: null,
    reactions: {},
    deletedBy: myUid,
    deletedForEveryoneAt: serverTimestamp(),
  });

  await updateConversationPreviewIfNeeded({
    convId,
    message,
    previousPreview,
    nextPreview: "Message deleted",
  });
}

export async function toggleReaction({ convId, messageId, myUid, emoji, currentReactions }) {
  if (!convId || !messageId || !myUid || !emoji) return;
  const next = {};
  for (const [key, value] of Object.entries(currentReactions || {})) {
    next[key] = Array.isArray(value) ? value.slice() : value;
  }
  const arr = Array.isArray(next[emoji])
    ? Array.from(new Set(next[emoji].filter((uid) => typeof uid === "string" && uid.length > 0)))
    : [];
  const idx = arr.indexOf(myUid);
  const action = idx >= 0 ? "remove" : "add";
  if (idx >= 0) arr.splice(idx, 1);
  else arr.push(myUid);
  if (arr.length > 0) next[emoji] = arr;
  else delete next[emoji];
  const ref = doc(db, "conversations", convId, "messages", messageId);
  try {
    await updateDoc(ref, {
      reactions: next,
      reactionOp: {
        emoji,
        uid: myUid,
        action,
      },
    });
  } catch (err) {
    if (!err || err.code !== "permission-denied") throw err;
    // Legacy fallback for older reaction maps that fail strict reactionOp validation.
    await updateDoc(ref, {
      reactions: next,
    });
  }
}

export async function setTyping(convId, uid, typing) {
  if (!convId || !uid) return;
  const ref = doc(db, "conversations", convId, "typing", uid);
  if (!typing) {
    await deleteDoc(ref).catch(() => {});
    return;
  }
  await setDoc(ref, { uid, updatedAt: serverTimestamp() }, { merge: true });
}

export function subscribeTyping(convId, cb, onError) {
  return onSnapshot(
    collection(db, "conversations", convId, "typing"),
    (snap) => {
      const now = Date.now();
      const rows = [];
      snap.forEach((d) => {
        const data = d.data();
        const ms = tsMs(data.updatedAt);
        if (data.uid && ms && now - ms < 6000) rows.push({ uid: data.uid, updatedAt: data.updatedAt });
      });
      cb(rows);
    },
    (err) => {
      try { err.where = "typing"; err.convId = convId; } catch (_) {}
      onError && onError(err);
    }
  );
}

export async function markConversationRead(convId, uid) {
  if (!convId || !uid) return;
  await setDoc(
    doc(db, "conversations", convId, "reads", uid),
    { uid, updatedAt: serverTimestamp() },
    { merge: true }
  );
}

export function subscribeReadReceipts(convId, cb, onError) {
  return onSnapshot(
    collection(db, "conversations", convId, "reads"),
    (snap) => {
      const out = {};
      snap.forEach((d) => {
        const data = d.data();
        if (data.uid) out[data.uid] = { uid: data.uid, updatedAt: data.updatedAt };
      });
      cb(out);
    },
    (err) => {
      try { err.where = "reads"; err.convId = convId; } catch (_) {}
      onError && onError(err);
    }
  );
}

// Bulk-fetch the chatProfiles for a list of uids (used for rendering names in groups).
export async function fetchProfilesByIds(uids) {
  const out = {};
  await Promise.all(
    uids.map(async (uid) => {
      const snap = await getDoc(doc(db, "chatProfiles", uid));
      if (snap.exists()) out[uid] = sanitizeChatProfile(uid, snap.data());
    })
  );
  return out;
}
